import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  Add,
  Colorize,
  DeleteOutline,
  Edit,
  LocalShipping,
  Payment,
  Restore,
  ShoppingCartOutlined,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import ModalImageCors from "../../components/ModalImageCors";
import LocationPreview from "../../components/LocationPreview";
import ChatbotModal from "../../components/ChatbotModal";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { buildWhatsappTabs, getWhatsappTabParams } from "../../utils/connectionsTabs";
import { AuthContext } from "../../context/Auth/AuthContext";
import { usePrivacyCensorSettings } from "../../utils/privacyCensor";
import ColorPicker, {
  isValidHexColor,
  normalizeHexColor,
  saveCustomColor,
} from "../../components/ColorPicker";

const ALL_ORDER_STATUSES_FILTER = "all-order-statuses-filter";
const ALL_PAYMENT_STATUSES_FILTER = "all-payment-statuses-filter";
const NO_SEQUENCE = "no-sequence";
const emptySalesFlowOption = () => ({
  label: "",
  sequenceId: NO_SEQUENCE,
});

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    margin: 20,
    padding: theme.spacing(1.5),
    overflow: "auto",
    ...theme.scrollbarStyles,
  },
  toolbar: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5),
  },
  tabsScroller: {
    maxWidth: "100%",
    minWidth: 0,
    "& .MuiTabs-scroller": {
      overflowX: "auto !important",
      WebkitOverflowScrolling: "touch",
      touchAction: "pan-x",
    },
    "& .MuiTabs-flexContainer": {
      flexWrap: "nowrap",
    },
  },
  settingsCard: {
    height: "100%",
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    backgroundColor: theme.palette.background.paper,
  },
  settingsActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  bulkActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0.5),
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1, 0),
      alignItems: "stretch",
      "& .MuiButton-root": {
        flex: "1 1 auto",
      },
    },
  },
  bulkActionsText: {
    fontWeight: 600,
    marginRight: "auto",
  },
  filters: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      "& .MuiFormControl-root": {
        flex: "1 1 100%",
      },
    },
  },
  table: {
    minWidth: 900,
  },
  tableWrapper: {
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  giftCardTableWrapper: {
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  mobileCards: {
    display: "none",
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gap: theme.spacing(1.5),
    },
  },
  mobileCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1.5),
  },
  mobileCardHeader: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  mobileOrderNumber: {
    fontWeight: 600,
    lineHeight: 1.25,
    wordBreak: "break-word",
  },
  mobileOrderCustomer: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  mobileCardGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(1.25),
  },
  mobileField: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minHeight: 32,
  },
  mobileLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  mobileValue: {
    minWidth: 0,
    textAlign: "right",
    wordBreak: "break-word",
  },
  mobileActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
  },
  sectionTitle: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
  rowFields: {
    display: "grid",
    gridTemplateColumns: "1fr 120px 120px auto",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  itemRow: {
    display: "grid",
    gridTemplateColumns: "auto minmax(260px, 2fr) repeat(3, minmax(96px, 1fr))",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      padding: theme.spacing(1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
    },
  },
  itemCatalogField: {
    minWidth: 0,
  },
  itemCatalogWideField: {
    minWidth: 0,
  },
  itemProductField: {
    minWidth: 0,
  },
  itemVariantField: {
    minWidth: 0,
  },
  itemCatalogVariantField: {
    minWidth: 0,
  },
  itemQuantityField: {
    minWidth: 0,
    width: "100%",
  },
  itemPriceField: {
    minWidth: 0,
    width: "100%",
  },
  itemDeleteButton: {
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      justifySelf: "flex-end",
    },
  },
  itemOptionsGrid: {
    gridColumn: "2 / -1",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 6,
    backgroundColor: theme.palette.action.hover,
    [theme.breakpoints.down("sm")]: {
      gridColumn: "auto",
    },
  },
  itemOptionControl: {
    minWidth: 0,
    width: "100%",
  },
  itemOptionPrice: {
    marginTop: theme.spacing(0.25),
    minHeight: 18,
  },
  productOptionBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    marginBottom: 30,
    minWidth: 0,
    overflow: "hidden",
  },
  checkoutOptionList: {
    marginTop: theme.spacing(1),
  },
  conditionalRow: {
    background: theme.palette.action.hover,
    borderRadius: 6,
    padding: theme.spacing(1.25),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  conditionalHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontWeight: 600,
  },
  conditionalFields: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  itemTotalBox: {
    marginTop: theme.spacing(1),
    minWidth: 0,
    width: "100%",
  },
  checkoutRow: {
    display: "grid",
    gridTemplateColumns: "180px minmax(260px, 1fr) auto",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      alignItems: "stretch",
      padding: theme.spacing(1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
    },
  },
  checkoutSettingsRow: {
    display: "grid",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
    },
  },
  checkoutSettingsFields: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1),
    minWidth: 0,
    "& .MuiFormControl-root": {
      minWidth: 0,
      width: "100%",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  checkoutSettingsControls: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(140px, 1.4fr) auto",
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(1),
    alignItems: "center",
    minWidth: 0,
    "& .MuiFormControlLabel-root": {
      minWidth: 0,
      marginRight: 0,
    },
    "& .MuiFormControlLabel-label": {
      minWidth: 0,
      overflowWrap: "anywhere",
      lineHeight: 1.25,
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr auto",
    },
  },
  checkoutOptions: {
    display: "grid",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  checkoutOptionRow: {
    display: "grid",
    gridTemplateColumns: "minmax(180px, 1fr) auto",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  checkoutDeleteButton: {
    justifySelf: "flex-end",
    alignSelf: "center",
    [theme.breakpoints.down("sm")]: {
      justifySelf: "flex-end",
    },
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: 6,
    border: "1px solid rgba(0,0,0,.12)",
  },
  colorAdornment: {
    width: 20,
    height: 20,
    borderRadius: 4,
    border: "1px solid rgba(0, 0, 0, 0.18)",
  },
  proofBox: {
    padding: theme.spacing(1),
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 6,
    display: "grid",
    gap: theme.spacing(1),
    height: "100%",
  },
  proofGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    alignItems: "stretch",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  proofPanel: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  locationBox: {
    padding: theme.spacing(1),
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 6,
    flex: 1,
    minHeight: 0,
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    "& > div": {
      minWidth: "0 !important",
      maxWidth: "100% !important",
      width: "100%",
      padding: "0 !important",
    },
  },
  proofPreview: {
    width: "100%",
    height: 140,
    cursor: "pointer",
    objectFit: "cover",
  },
  proofPdfPreview: {
    border: 0,
    width: "100%",
    height: 140,
  },
  proofContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  proofName: {
    display: "block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  proofActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  proofPreviewBox: {
    minHeight: 142,
    border: "1px dashed rgba(0,0,0,.12)",
    borderRadius: 6,
    padding: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  productMessagePreview: {
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 6,
    padding: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    background: "#fff",
  },
  productPreviewHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    marginBottom: theme.spacing(1),
  },
  productPreviewBody: {
    display: "flex",
    justifyContent: "center",
    background: "#f7f7f7",
    borderRadius: 6,
    padding: theme.spacing(1),
  },
  productBubble: {
    maxWidth: 280,
    width: "100%",
    borderRadius: 6,
    background: "#fff",
    overflow: "hidden",
    border: "1px solid rgba(0,0,0,.08)",
  },
  productPreviewImage: {
    display: "block",
    width: "100%",
    height: 150,
    objectFit: "cover",
    background: "#eceff1",
  },
  productPreviewText: {
    padding: theme.spacing(1),
    whiteSpace: "pre-line",
  },
  productFileActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginTop: theme.spacing(1),
  },
  productFilePicker: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    minWidth: 0,
  },
  productFileName: {
    maxWidth: 280,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  paymentMethodActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: theme.spacing(0.5),
  },
  censoredText: {
    filter: "blur(4px)",
    userSelect: "none",
  },
  censoredPhoto: {
    filter: "blur(6px)",
  },
}));

const colors = [
  "#E8F3FF",
  "#E7F8EF",
  "#F0ECFF",
  "#FFF0E6",
  "#FFEFF4",
  "#E9F7F8",
  "#EEF2F7",
];

const paymentStatuses = {
  pending: "Pendiente",
  partial: "Parcial",
  proof_received: "Comprobante recibido",
  reviewing: "En revision",
  paid: "Pagado",
  rejected: "Rechazado",
  refunded: "Reembolsado",
};

const emptyMethod = {
  id: null,
  name: "",
  description: "",
  instructions: "",
  requiresProof: false,
  isActive: true,
};

const emptyStatus = {
  id: null,
  name: "",
  key: "",
  color: colors[0],
  position: 0,
  isFinal: false,
  triggersStreamingFulfillment: false,
  isActive: true,
  sequenceId: "",
};

const emptyShippingMethod = {
  id: null,
  name: "",
  description: "",
  cost: 0,
  fields: [],
  requiresLocation: false,
  isActive: true,
};

const emptyCategory = {
  id: null,
  name: "",
  description: "",
  imageUrl: "",
  position: 0,
  isActive: true,
};

const conditionOperators = [
  { value: "equals", label: "Es igual" },
  { value: "not_equals", label: "No es igual" },
  { value: "contains", label: "Contiene" },
];

const checkoutFieldTypes = [
  { value: "text", label: "Texto" },
  { value: "number", label: "Numero" },
  { value: "date", label: "Fecha" },
  { value: "email", label: "Correo" },
  { value: "phone", label: "Telefono" },
  { value: "textarea", label: "Texto largo" },
  { value: "select", label: "Desplegable" },
  { value: "multiple", label: "Multiple" },
];

const emptyConditionalConfig = () => ({
  enabled: false,
  sourceOptionName: "",
  operator: "equals",
  value: "",
});

const emptyCheckoutConditionalConfig = () => ({
  enabled: false,
  sourceFieldName: "",
  operator: "equals",
  value: "",
});

const emptyShippingField = () => ({
  name: "",
  value: "",
  type: "text",
  required: true,
  options: [{ name: "", priceDelta: 0 }],
  conditionalConfig: emptyCheckoutConditionalConfig(),
});

const emptyProductOption = () => ({
  name: "",
  required: false,
  multiple: false,
  conditionalConfig: emptyConditionalConfig(),
  values: [{ name: "", priceDelta: 0 }],
});

const emptyStreamingConfigItem = () => ({
  platformId: "no-streaming-platform",
  accessType: "profile",
  durationMode: "fixed",
  durationDays: 30,
  optionName: "no-streaming-option",
});

const emptyGiftCardConfig = {
  enabled: false,
  action: "create",
  amountMode: "price",
  amount: 0,
  optionName: "no-gift-card-option",
  sendAccess: true,
};

const emptySalesFlowConfig = {
  enabled: false,
  options: [emptySalesFlowOption()],
};

const emptyProduct = {
  id: null,
  name: "",
  sku: "",
  description: "",
  price: 0,
  imageUrl: "",
  stock: 999999,
  position: 0,
  isActive: true,
  categoryId: "",
  options: [],
  streamingConfig: {
    enabled: false,
    sendAccess: true,
    items: [emptyStreamingConfigItem()],
  },
  giftCardConfig: emptyGiftCardConfig,
  salesFlowConfig: emptySalesFlowConfig,
};

const emptyUpsell = {
  id: null,
  name: "",
  message: "",
  offeredProductId: "no-catalog-product",
  targetProductIds: [],
  targetCategoryIds: [],
  quantity: 1,
  position: 0,
  isActive: true,
};

const defaultCheckoutFields = [
  { name: "Nombre", value: "" },
  { name: "Teléfono", value: "" },
  { name: "Dirección exacta", value: "" },
  { name: "Referencias", value: "" },
];

const emptySettings = {
  currencySymbol: "$",
  decimalPlaces: 2,
  checkoutFields: defaultCheckoutFields,
  paidStatusKey: "",
  partiallyPaidStatusKey: "",
  giftCardPaymentMethodId: "",
  orderConfirmationSequenceId: NO_SEQUENCE,
};

const emptyGiftCard = {
  id: null,
  code: "",
  initialBalance: 0,
  balance: 0,
  expiresAt: "",
  notes: "",
  isActive: true,
};

const NO_PAYMENT_METHOD = "no-payment-method";
const NO_SHIPPING_METHOD = "no-shipping-method";
const NO_CATALOG_PRODUCT = "no-catalog-product";
const NO_PRODUCT_VARIANT = "no-product-variant";
const NO_STREAMING_PLATFORM = "no-streaming-platform";
const NO_STREAMING_OPTION = "no-streaming-option";
const NO_GIFT_CARD_OPTION = "no-gift-card-option";
const NO_PRODUCT_OPTION_CONDITION = "no-product-option-condition";
const NO_SHIPPING_FIELD_CONDITION = "no-shipping-field-condition";
const ALL_UPSELL_PRODUCTS = "all-upsell-products";
const ALL_UPSELL_CATEGORIES = "all-upsell-categories";

const toOptionalValue = (value, emptyValue) => (value === emptyValue ? "" : value || "");

const normalizeFieldName = value =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const isDeliveryLocationField = field =>
  normalizeFieldName(field?.name) === "ubicacion de entrega";

const isPdfFile = ({ mimeType = "", name = "", url = "" } = {}) =>
  /^application\/pdf/i.test(mimeType || "") || /\.pdf($|\?)/i.test(name || url || "");

const isImageFile = ({ mimeType = "", name = "", url = "" } = {}) =>
  /^image\//i.test(mimeType || "") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(name || url || "");

const buildFileHref = url => {
  if (!url) return "";
  if (/^(https?:|blob:)/i.test(url)) return url;
  return `${String(api.defaults.baseURL || "").replace(/\/+$/, "")}/${String(url).replace(/^\/+/, "")}`;
};

const getFileNameFromUrl = url => {
  const cleanUrl = String(url || "").split("?")[0];
  return cleanUrl.split("/").filter(Boolean).pop() || "";
};

const normalizeCheckoutConditionalPayload = config => {
  const nextConfig = {
    ...emptyCheckoutConditionalConfig(),
    ...(config || {}),
  };

  return {
    enabled: nextConfig.enabled === true,
    sourceFieldName: String(nextConfig.sourceFieldName || "").trim(),
    operator: conditionOperators.some(operator => operator.value === nextConfig.operator)
      ? nextConfig.operator
      : "equals",
    value: String(nextConfig.value || "").trim(),
  };
};

const normalizeCheckoutFieldOptions = (options, keepEmpty = false) =>
  (Array.isArray(options) ? options : [])
    .map(option => (typeof option === "string" ? option : option?.label || option?.name || ""))
    .map(option => String(option || "").trim())
    .filter(option => keepEmpty || option);

const normalizeCheckoutFieldType = type =>
  checkoutFieldTypes.some(fieldType => fieldType.value === type) ? type : "text";

const normalizeCheckoutFields = fields =>
  (fields?.length ? fields : defaultCheckoutFields).map(field => ({
    name: field.name || "",
    value: field.value || "",
    type: normalizeCheckoutFieldType(field.type || "text"),
    required: field.required !== false,
    confirmValue: field.confirmValue !== false,
    options: normalizeCheckoutFieldOptions(field.options, true),
    conditionalConfig: normalizeCheckoutConditionalPayload(field.conditionalConfig),
  }));

const normalizeShippingFieldOptions = (options, keepEmpty = false) =>
  (Array.isArray(options) ? options : [])
    .map(option => (
      typeof option === "string"
        ? { name: option, priceDelta: 0 }
        : { name: option?.name || option?.label || "", priceDelta: option?.priceDelta || 0 }
    ))
    .map(option => ({
      name: keepEmpty ? String(option.name || "") : String(option.name || "").trim(),
      priceDelta: keepEmpty ? option.priceDelta ?? 0 : Number(option.priceDelta || 0),
    }))
    .filter(option => keepEmpty || option.name.trim());

const normalizeShippingFields = fields =>
  (fields || []).map(field => ({
    ...emptyShippingField(),
    ...field,
    name: field.name || "",
    value: field.value || "",
    type: normalizeCheckoutFieldType(field.type || "text"),
    required: field.required !== false,
    options: normalizeShippingFieldOptions(
      field.options?.length ? field.options : [{ name: "", priceDelta: 0 }],
      true
    ),
    conditionalConfig: normalizeCheckoutConditionalPayload(field.conditionalConfig),
  }));

const sanitizeShippingFields = fields =>
  normalizeShippingFields(fields)
    .map(field => ({
      name: field.name.trim(),
      value: "",
      type: normalizeCheckoutFieldType(field.type || "text"),
      required: field.required !== false,
      options: normalizeShippingFieldOptions(field.options),
      conditionalConfig: normalizeCheckoutConditionalPayload(field.conditionalConfig),
    }))
    .filter(field => field.name);

const buildCheckoutFieldsFromSettings = settings =>
  normalizeCheckoutFields(settings?.checkoutFields).map(field => ({ ...field }));

const emptyOrderItem = {
  catalogProductId: NO_CATALOG_PRODUCT,
  productName: "",
  catalogOptions: [],
  optionSelections: {},
  basePrice: 0,
  variantName: NO_PRODUCT_VARIANT,
  quantity: 1,
  unitPrice: 0,
  notes: "",
};

const emptyOrder = {
  id: null,
  status: "new",
  paymentStatus: "pending",
  paymentMethodId: NO_PAYMENT_METHOD,
  shippingMethodId: NO_SHIPPING_METHOD,
  customerName: "",
  customerPhone: "",
  customerEmail: "",
  discount: 0,
  tax: 0,
  shipping: 0,
  customerNotes: "",
  internalNotes: "",
  checkoutFields: [],
  items: [emptyOrderItem],
};

const formatMoney = (value, settings = emptySettings) =>
  Number(value || 0).toLocaleString("es-CR", {
    minimumFractionDigits: Number(settings.decimalPlaces || 0),
    maximumFractionDigits: Number(settings.decimalPlaces || 0),
  });

const normalizeItems = items =>
  (items || []).map(item => ({
    catalogProductId: item.catalogProductId || item.productId || item.product?.id || NO_CATALOG_PRODUCT,
    productName: item.productName || item.product?.name || "",
    catalogOptions: item.catalogOptions || item.product?.options || [],
    optionSelections: item.optionSelections || {},
    basePrice: Number(item.basePrice || item.product?.price || item.unitPrice || 0),
    variantName: item.variantName || NO_PRODUCT_VARIANT,
    quantity: Number(item.quantity || 1),
    unitPrice: Number(item.unitPrice || 0),
    notes: item.notes || "",
  }));

const sanitizeOrderItems = items =>
  (items || []).map(item => {
    const optionSelections = cleanHiddenProductSelections(
      item.catalogOptions || [],
      item.optionSelections || {}
    );
    const nextItem = { ...item, optionSelections };

    return {
      productName: item.productName,
      sku: item.sku || "",
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      notes: item.notes || "",
      productId: toOptionalValue(item.catalogProductId, NO_CATALOG_PRODUCT),
      catalogProductId: toOptionalValue(item.catalogProductId, NO_CATALOG_PRODUCT),
      optionSelections,
      variantName: toOptionalValue(
        buildItemOptionText(nextItem) || item.variantName,
        NO_PRODUCT_VARIANT
      ),
    };
  });

const normalizeSelectionArray = value => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const normalizeProductConditionalPayload = config => {
  const nextConfig = {
    ...emptyConditionalConfig(),
    ...(config || {}),
  };

  return {
    enabled: nextConfig.enabled === true,
    sourceOptionName: String(nextConfig.sourceOptionName || "").trim(),
    operator: conditionOperators.some(operator => operator.value === nextConfig.operator)
      ? nextConfig.operator
      : "equals",
    value: String(nextConfig.value || "").trim(),
  };
};

const normalizeProductOptionsForForm = options =>
  (options || []).map(option => ({
    ...emptyProductOption(),
    ...option,
    conditionalConfig: normalizeProductConditionalPayload(option.conditionalConfig),
    values: (option.values?.length ? option.values : [{ name: "", priceDelta: 0 }]).map(value => ({
      name: value.name || "",
      priceDelta: value.priceDelta || 0,
    })),
  }));

const normalizeSalesFlowConfigForForm = config => {
  const options = (config?.options?.length
    ? config.options
    : [emptySalesFlowOption()]
  ).map(option => ({
    label: option.label || "",
    sequenceId: option.sequenceId ? String(option.sequenceId) : NO_SEQUENCE,
  }));

  return {
    ...emptySalesFlowConfig,
    ...(config || {}),
    enabled: config?.enabled === true,
    options,
  };
};

const normalizeComparableText = value =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const productOptionMatchesCondition = (option, selections = {}) => {
  const config = normalizeProductConditionalPayload(option?.conditionalConfig);
  if (!config.enabled) return true;
  if (!config.sourceOptionName || !config.value) return true;

  const selected = selections[config.sourceOptionName];
  const selectedValues = normalizeSelectionArray(selected).filter(Boolean);
  if (!selectedValues.length) return false;
  const expectedValue = normalizeComparableText(config.value);
  const valuesToCompare = selectedValues.map(value => normalizeComparableText(value));

  if (config.operator === "not_equals") {
    return !valuesToCompare.some(value => value === expectedValue);
  }

  if (config.operator === "contains") {
    return valuesToCompare.some(value => value.includes(expectedValue));
  }

  return valuesToCompare.some(value => value === expectedValue);
};

const getVisibleProductOptions = (options, selections = {}) =>
  (options || []).filter(option => productOptionMatchesCondition(option, selections));

const cleanHiddenProductSelections = (options, selections = {}) => {
  const visibleNames = new Set(getVisibleProductOptions(options, selections).map(option => option.name));
  return Object.entries(selections || {}).reduce((acc, [name, value]) => {
    if (visibleNames.has(name)) acc[name] = value;
    return acc;
  }, {});
};

const getOptionValues = option => option?.values || [];

const getSelectedOptionValues = (option, selection) => {
  const selectedNames = normalizeSelectionArray(selection);
  return getOptionValues(option).filter(value => selectedNames.includes(value.name));
};

const getOptionExtra = (option, selection) =>
  getSelectedOptionValues(option, selection).reduce(
    (sum, value) => sum + Number(value.priceDelta || 0),
    0
  );

const calculateItemUnitPrice = item =>
  Number(item.basePrice || 0) +
  getVisibleProductOptions(item.catalogOptions || [], item.optionSelections || {}).reduce(
    (sum, option) => sum + getOptionExtra(option, item.optionSelections?.[option.name]),
    0
  );

const buildItemOptionText = item =>
  getVisibleProductOptions(item.catalogOptions || [], item.optionSelections || {})
    .map(option => {
      const selected = item.optionSelections?.[option.name];
      const selectedValues = normalizeSelectionArray(selected).filter(Boolean);
      if (!selectedValues.length) return "";
      return `${option.name}: ${selectedValues.join(", ")}`;
    })
    .filter(Boolean)
    .join(" | ");

const formatSequenceOption = sequence => `ID: ${sequence.id} - ${sequence.name}`;

const Orders = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const privacyCensorSettings = usePrivacyCensorSettings(user?.id);
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [streamingPlatforms, setStreamingPlatforms] = useState([]);
  const [upsells, setUpsells] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [orderSettings, setOrderSettings] = useState(emptySettings);
  const [connections, setConnections] = useState([]);
  const [orphanGroups, setOrphanGroups] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [selectedOrderContact, setSelectedOrderContact] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [methodModalOpen, setMethodModalOpen] = useState(false);
  const [methodModalContext, setMethodModalContext] = useState(null);
  const [shippingMethodModalOpen, setShippingMethodModalOpen] = useState(false);
  const [shippingMethodModalContext, setShippingMethodModalContext] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [upsellModalOpen, setUpsellModalOpen] = useState(false);
  const [giftCardModalOpen, setGiftCardModalOpen] = useState(false);
  const [orderForm, setOrderForm] = useState(emptyOrder);
  const [methodForm, setMethodForm] = useState(emptyMethod);
  const [shippingMethodForm, setShippingMethodForm] = useState(emptyShippingMethod);
  const [statusForm, setStatusForm] = useState(emptyStatus);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [productForm, setProductForm] = useState(emptyProduct);
  const [upsellForm, setUpsellForm] = useState(emptyUpsell);
  const [giftCardForm, setGiftCardForm] = useState(emptyGiftCard);
  const [statusColorPickerOpen, setStatusColorPickerOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState(emptySettings);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [deletingMethod, setDeletingMethod] = useState(null);
  const [deletingShippingMethod, setDeletingShippingMethod] = useState(null);
  const [deletingStatus, setDeletingStatus] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [deletingUpsell, setDeletingUpsell] = useState(null);
  const [deletingGiftCard, setDeletingGiftCard] = useState(null);
  const [deletingCheckoutFieldIndex, setDeletingCheckoutFieldIndex] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [templateResetOpen, setTemplateResetOpen] = useState(false);
  const [templateResetting, setTemplateResetting] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [proofFilePreviewUrl, setProofFilePreviewUrl] = useState("");
  const [proofRemoved, setProofRemoved] = useState(false);
  const [categoryImageFile, setCategoryImageFile] = useState(null);
  const [categoryImagePreviewUrl, setCategoryImagePreviewUrl] = useState("");
  const [productImageFile, setProductImageFile] = useState(null);
  const [productImagePreviewUrl, setProductImagePreviewUrl] = useState("");
  const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
  const [sequenceModalId, setSequenceModalId] = useState(null);
  const sequenceModalApplySelection = useRef(null);

  const statusByKey = useMemo(
    () =>
      statuses.reduce((acc, status) => {
        acc[status.key] = status;
        return acc;
      }, {}),
    [statuses]
  );
  const whatsappTabs = useMemo(
    () => buildWhatsappTabs(connections, orphanGroups),
    [connections, orphanGroups]
  );
  const selectedWhatsappTab = whatsappTabs.find(
    tab => String(tab.value) === String(selectedConnectionId)
  );
  const selectedConnectionParams = useMemo(
    () => getWhatsappTabParams(selectedConnectionId),
    [selectedConnectionId]
  );

  useEffect(() => {
    if (!proofFile) {
      setProofFilePreviewUrl("");
      return undefined;
    }

    const nextUrl = window.URL.createObjectURL(proofFile);
    setProofFilePreviewUrl(nextUrl);
    return () => window.URL.revokeObjectURL(nextUrl);
  }, [proofFile]);

  useEffect(() => {
    if (!productImageFile) {
      setProductImagePreviewUrl("");
      return undefined;
    }

    const nextUrl = window.URL.createObjectURL(productImageFile);
    setProductImagePreviewUrl(nextUrl);
    return () => window.URL.revokeObjectURL(nextUrl);
  }, [productImageFile]);

  useEffect(() => {
    if (!categoryImageFile) {
      setCategoryImagePreviewUrl("");
      return undefined;
    }

    const nextUrl = window.URL.createObjectURL(categoryImageFile);
    setCategoryImagePreviewUrl(nextUrl);
    return () => window.URL.revokeObjectURL(nextUrl);
  }, [categoryImageFile]);

  const contactOptions = useMemo(() => {
    if (!selectedOrderContact) return contacts;
    const exists = contacts.some(
      contact => String(contact.id) === String(selectedOrderContact.id)
    );
    return exists ? contacts : [selectedOrderContact, ...contacts];
  }, [contacts, selectedOrderContact]);

  const renderContactOption = option => (
    <>
      <Avatar
        src={privacyCensorSettings.contactPhotos ? undefined : option.profilePicUrl}
        className={privacyCensorSettings.contactPhotos ? classes.censoredPhoto : undefined}
        style={{ marginRight: 8 }}
      />
      <div>
        <Typography
          variant="body2"
          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
        >
          {privacyCensorSettings.contactNames ? "Contacto privado" : option.name || "-"}
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
        >
          {privacyCensorSettings.contactNames ? "Telefono privado" : option.number || ""}
        </Typography>
      </div>
    </>
  );

  const orderSubtotal = useMemo(
    () =>
      (orderForm.items || []).reduce(
        (sum, item) => sum + Number(item.quantity || 0) * Number(item.unitPrice || 0),
        0
      ),
    [orderForm.items]
  );
  const orderTotal =
    orderSubtotal -
    Number(orderForm.discount || 0) +
    Number(orderForm.tax || 0) +
    Number(orderForm.shipping || 0);

  const filteredOrders = useMemo(() => {
    const search = orderSearch.trim().toLowerCase();
    if (!search) return orders;

    return orders.filter(order => {
      const statusName = statusByKey[order.status]?.name || order.status || "";
      const values = [
        order.number,
        order.customerName,
        order.customerPhone,
        order.customerEmail,
        order.contact?.name,
        order.contact?.number,
        order.contact?.email,
        statusName,
        paymentStatuses[order.paymentStatus] || order.paymentStatus,
        order.paymentMethod?.name,
        order.total,
        ...(order.items || []).map(item => item.productName),
      ];

      return values
        .filter(value => value !== undefined && value !== null)
        .some(value => String(value).toLowerCase().includes(search));
    });
  }, [orders, orderSearch, statusByKey]);

  const fetchBase = async () => {
    if (!selectedConnectionId || selectedWhatsappTab?.orphan) {
      setProducts([]);
      setProductCategories([]);
      setStreamingPlatforms([]);
      setUpsells([]);
      setGiftCards([]);
      setPaymentMethods([]);
      setShippingMethods([]);
      return;
    }
    try {
      const [
        { data: productData },
        { data: categoryData },
        { data: upsellData },
        { data: giftCardData },
        { data: methodData },
        { data: shippingMethodData },
        { data: statusData },
        { data: settingsData },
        { data: sequenceData },
        { data: streamingPlatformData },
      ] =
        await Promise.all([
          api.get("/orders/products", { params: selectedConnectionParams }),
          api.get("/orders/product-categories", { params: selectedConnectionParams }),
          api.get("/orders/upsells", { params: selectedConnectionParams }),
          api.get("/orders/gift-cards", { params: selectedConnectionParams }),
          api.get("/orders/payment-methods", { params: selectedConnectionParams }),
          api.get("/orders/shipping-methods", { params: selectedConnectionParams }),
          api.get("/orders/statuses"),
          api.get("/orders/settings", { params: selectedConnectionParams }),
          api.get("/chatbot/sequences", { params: { all: true, pageNumber: 1, searchParam: "" } }),
          api.get("/streaming/platforms"),
        ]);
      setProducts(productData.products || []);
      setProductCategories(categoryData.categories || []);
      setStreamingPlatforms(streamingPlatformData.platforms || []);
      setUpsells(upsellData.upsells || []);
      setGiftCards(giftCardData.giftCards || []);
      setPaymentMethods(methodData.paymentMethods || []);
      setShippingMethods(shippingMethodData.shippingMethods || []);
      setStatuses(statusData.statuses || []);
      setOrderSettings(settingsData.settings || emptySettings);
      setSettingsForm(settingsData.settings || emptySettings);
      setSequences(sequenceData.sequences || []);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchOrders = async () => {
    if (!selectedConnectionId || selectedWhatsappTab?.orphan) {
      setOrders([]);
      return;
    }
    try {
      const { data } = await api.get("/orders", {
        params: {
          ...selectedConnectionParams,
          status: statusFilter || undefined,
          paymentStatus: paymentFilter || undefined,
        },
      });
      setOrders(data.orders || []);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const [{ data }, { data: orphanData }] = await Promise.all([
          api.get("/connections/"),
          api.get("/connections/orphan-groups")
        ]);
        const loadedConnections = data || [];
        const loadedOrphans = orphanData || [];
        const tabs = buildWhatsappTabs(loadedConnections, loadedOrphans);
        setConnections(loadedConnections);
        setOrphanGroups(loadedOrphans);
        setSelectedConnectionId(prevState => prevState || tabs[0]?.value || "");
      } catch (err) {
        toastError(err);
      }
    };

    fetchConnections();
  }, []);

  useEffect(() => {
    fetchBase();
    fetchOrders();
    setSelectedOrderIds([]);
  }, [selectedConnectionId, statusFilter, paymentFilter]);

  useEffect(() => {
    const orderIds = new Set(orders.map(order => String(order.id)));
    setSelectedOrderIds(prevState =>
      prevState.filter(orderId => orderIds.has(String(orderId)))
    );
  }, [orders]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", {
          params: { searchParam: contactSearch, pageNumber: 1 },
        });
        setContacts(data.contacts || []);
      } catch (err) {
        toastError(err);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [contactSearch]);

  useEffect(() => {
    const socket = openSocket();
    socket.on("orders", data => {
      if (["create", "update", "delete"].includes(data.action)) fetchOrders();
    });
    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnectionId, statusFilter, paymentFilter]);

  const openOrderModal = order => {
    setProofFile(null);
    setProofRemoved(false);
    setSelectedOrderContact(order?.contact || null);
    setOrderForm(
      order
        ? {
            ...emptyOrder,
            ...order,
            connectionId: order.connectionId || selectedConnectionId,
            paymentMethodId: order.paymentMethodId || NO_PAYMENT_METHOD,
            shippingMethodId: order.shippingMethodId || NO_SHIPPING_METHOD,
            items: normalizeItems(order.items),
            checkoutFields: order.checkoutFields || [],
          }
      : {
          ...emptyOrder,
          items: [{ ...emptyOrderItem }],
          checkoutFields: buildCheckoutFieldsFromSettings(orderSettings),
          status: statuses[0]?.key || "new",
          connectionId: selectedConnectionId,
          shippingMethodId: NO_SHIPPING_METHOD,
        }
    );
    setOrderModalOpen(true);
  };

  const saveOrder = async () => {
    try {
      const payload = {
        ...orderForm,
        contactId: selectedOrderContact?.id || orderForm.contactId || null,
        discount: Number(orderForm.discount || 0),
        tax: Number(orderForm.tax || 0),
        shipping: Number(orderForm.shipping || 0),
        items: sanitizeOrderItems(orderForm.items),
        connectionId: orderForm.connectionId || selectedConnectionId,
        paymentMethodId: toOptionalValue(orderForm.paymentMethodId, NO_PAYMENT_METHOD),
        shippingMethodId: toOptionalValue(orderForm.shippingMethodId, NO_SHIPPING_METHOD),
        clearPaymentProof: proofRemoved,
      };
      const { data } = orderForm.id
        ? await api.put(`/orders/${orderForm.id}`, payload)
        : await api.post("/orders", payload);

      if (proofFile) {
        const formData = new FormData();
        formData.append("paymentProof", proofFile);
        await api.post(`/orders/${data.id}/payment-proof`, formData);
      }

      toast.success("Pedido guardado");
      setOrderModalOpen(false);
      fetchOrders();
    } catch (err) {
      toastError(err);
    }
  };

  const closeMethodModal = () => {
    setMethodModalOpen(false);
    setMethodModalContext(null);
  };

  const closeShippingMethodModal = () => {
    setShippingMethodModalOpen(false);
    setShippingMethodModalContext(null);
  };

  const openCreatePaymentMethod = context => {
    if (!selectedConnectionId || selectedWhatsappTab?.orphan) {
      toast.error("Selecciona una conexion activa para crear metodos de pago.");
      return;
    }

    setMethodForm({ ...emptyMethod, connectionId: selectedConnectionId });
    setMethodModalContext(context || null);
    setMethodModalOpen(true);
  };

  const openEditPaymentMethod = (methodId, context) => {
    if (!methodId || methodId === NO_PAYMENT_METHOD) return;
    if (!selectedConnectionId || selectedWhatsappTab?.orphan) {
      toast.error("Selecciona una conexion activa para editar metodos de pago.");
      return;
    }

    const method = paymentMethods.find(
      entry => String(entry.id) === String(methodId)
    );
    if (!method) return;

    setMethodForm({ ...emptyMethod, ...method });
    setMethodModalContext(context || null);
    setMethodModalOpen(true);
  };

  const saveMethod = async () => {
    try {
      const payload = {
        ...methodForm,
        connectionId: methodForm.connectionId || selectedConnectionId,
      };
      let savedMethod;
      if (methodForm.id) {
        const { data } = await api.put(`/orders/payment-methods/${methodForm.id}`, payload);
        savedMethod = data;
      } else {
        const { data } = await api.post("/orders/payment-methods", payload);
        savedMethod = data;
      }
      toast.success("Metodo de pago guardado");
      if (methodModalContext === "order" && savedMethod?.id) {
        setOrderForm(prevState => ({
          ...prevState,
          paymentMethodId: savedMethod.id,
        }));
      }
      closeMethodModal();
      fetchBase();
    } catch (err) {
      toastError(err);
    }
  };

  const saveStatus = async () => {
    try {
      const payload = {
        ...statusForm,
        sequenceId: toOptionalValue(statusForm.sequenceId, NO_SEQUENCE),
        key:
          statusForm.key ||
          statusForm.name
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "_"),
      };
      if (statusForm.id) {
        await api.put(`/orders/statuses/${statusForm.id}`, payload);
      } else {
        await api.post("/orders/statuses", payload);
      }
      toast.success("Estado guardado");
      setStatusModalOpen(false);
      fetchBase();
    } catch (err) {
      toastError(err);
    }
  };

  const upsertSequence = sequence => {
    if (!sequence?.id) return;
    setSequences(prevSequences => {
      const exists = prevSequences.some(item => String(item.id) === String(sequence.id));
      return exists
        ? prevSequences.map(item => (String(item.id) === String(sequence.id) ? sequence : item))
        : [...prevSequences, sequence];
    });
  };

  const openCreateSequence = applySelection => {
    setSequenceModalId(null);
    sequenceModalApplySelection.current = applySelection;
    setSequenceModalOpen(true);
  };

  const openEditSequence = (sequenceId, applySelection) => {
    if (!sequenceId || sequenceId === NO_SEQUENCE) return;
    setSequenceModalId(sequenceId);
    sequenceModalApplySelection.current = applySelection;
    setSequenceModalOpen(true);
  };

  const handleSequenceSaved = savedSequence => {
    const savedSequenceId = savedSequence?.id || sequenceModalId;
    upsertSequence(savedSequence);
    if (savedSequenceId && sequenceModalApplySelection.current) {
      sequenceModalApplySelection.current(String(savedSequenceId));
    }
    setSequenceModalOpen(false);
    setSequenceModalId(null);
    sequenceModalApplySelection.current = null;
  };

  const handleSequenceModalClose = () => {
    setSequenceModalOpen(false);
    setSequenceModalId(null);
    sequenceModalApplySelection.current = null;
  };

  const saveSettings = async () => {
    try {
      const checkoutFields = normalizeCheckoutFields(settingsForm.checkoutFields)
        .map(field => ({
          ...field,
          options: normalizeCheckoutFieldOptions(field.options),
          conditionalConfig: normalizeCheckoutConditionalPayload(field.conditionalConfig),
        }))
        .filter(field => field.name);
      const { data } = await api.put("/orders/settings", {
        ...settingsForm,
        checkoutFields,
        orderConfirmationSequenceId: toOptionalValue(
          settingsForm.orderConfirmationSequenceId,
          NO_SEQUENCE
        ),
        connectionId: selectedConnectionId,
      });
      setOrderSettings(data.settings || emptySettings);
      setSettingsForm(data.settings || emptySettings);
      toast.success("Configuracion guardada");
    } catch (err) {
      toastError(err);
    }
  };

  const deleteCheckoutField = async () => {
    if (deletingCheckoutFieldIndex === null || deletingCheckoutFieldIndex === undefined) {
      return;
    }

    try {
      const checkoutFields = normalizeCheckoutFields(settingsForm.checkoutFields).filter(
        (_, fieldIndex) => fieldIndex !== deletingCheckoutFieldIndex
      );
      const { data } = await api.put("/orders/settings", {
        ...settingsForm,
        checkoutFields,
        connectionId: selectedConnectionId,
      });
      setOrderSettings(data.settings || emptySettings);
      setSettingsForm(data.settings || emptySettings);
      setDeletingCheckoutFieldIndex(null);
      toast.success("Campo eliminado");
    } catch (err) {
      toastError(err);
    }
  };

  const saveGiftCard = async () => {
    try {
      const payload = {
        ...giftCardForm,
        code: String(giftCardForm.code || "").trim().toUpperCase(),
        initialBalance: Number(giftCardForm.initialBalance || 0),
        balance: Number(giftCardForm.balance || 0),
        expiresAt: giftCardForm.expiresAt || null,
        connectionId: selectedConnectionId,
      };
      if (giftCardForm.id) {
        await api.put(`/orders/gift-cards/${giftCardForm.id}`, payload);
      } else {
        await api.post("/orders/gift-cards", payload);
      }
      toast.success("Tarjeta guardada");
      setGiftCardModalOpen(false);
      fetchBase();
    } catch (err) {
      toastError(err);
    }
  };

  const openCreateShippingMethod = context => {
    if (!selectedConnectionId || selectedWhatsappTab?.orphan) {
      toast.error("Selecciona una conexion activa para crear metodos de envio.");
      return;
    }

    setShippingMethodForm({ ...emptyShippingMethod, connectionId: selectedConnectionId });
    setShippingMethodModalContext(context || null);
    setShippingMethodModalOpen(true);
  };

  const openEditShippingMethod = (methodId, context) => {
    if (!methodId || methodId === NO_SHIPPING_METHOD) return;
    if (!selectedConnectionId || selectedWhatsappTab?.orphan) {
      toast.error("Selecciona una conexion activa para editar metodos de envio.");
      return;
    }

    const method = shippingMethods.find(
      entry => String(entry.id) === String(methodId)
    );
    if (!method) return;

    setShippingMethodForm({
      ...emptyShippingMethod,
      ...method,
      fields: normalizeShippingFields(method.fields),
    });
    setShippingMethodModalContext(context || null);
    setShippingMethodModalOpen(true);
  };

  const saveShippingMethod = async () => {
    try {
      const payload = {
        ...shippingMethodForm,
        connectionId: shippingMethodForm.connectionId || selectedConnectionId,
        cost: Number(shippingMethodForm.cost || 0),
        fields: sanitizeShippingFields(shippingMethodForm.fields),
      };
      let savedMethod;
      if (shippingMethodForm.id) {
        const { data } = await api.put(
          `/orders/shipping-methods/${shippingMethodForm.id}`,
          payload
        );
        savedMethod = data;
      } else {
        const { data } = await api.post("/orders/shipping-methods", payload);
        savedMethod = data;
      }
      toast.success("Metodo de envio guardado");
      if (shippingMethodModalContext === "order" && savedMethod?.id) {
        setOrderForm(prevState => ({
          ...prevState,
          shippingMethodId: savedMethod.id,
          shipping: Number(savedMethod.cost || 0),
        }));
      }
      closeShippingMethodModal();
      fetchBase();
    } catch (err) {
      toastError(err);
    }
  };

  const resetTemplate = async () => {
    if (!selectedConnectionId || selectedWhatsappTab?.orphan) {
      toast.error("Selecciona una conexion activa para restablecer la plantilla.");
      return;
    }

    setTemplateResetting(true);
    try {
      await api.post("/orders/template/reset", {
        connectionId: selectedConnectionId,
      });
      toast.success("Plantilla restablecida");
      setTemplateResetOpen(false);
      await fetchBase();
      await fetchOrders();
    } catch (err) {
      toastError(err);
    } finally {
      setTemplateResetting(false);
    }
  };

  const saveCategory = async () => {
    try {
      let imageUrl = categoryForm.imageUrl || "";
      if (categoryImageFile) {
        const formData = new FormData();
        formData.append("categoryImage", categoryImageFile);
        const { data } = await api.post("/orders/product-categories/image", formData);
        imageUrl = data.imageUrl || imageUrl;
      }

      const payload = { ...categoryForm, imageUrl, connectionId: selectedConnectionId };
      if (categoryForm.id) {
        await api.put(`/orders/product-categories/${categoryForm.id}`, payload);
      } else {
        await api.post("/orders/product-categories", payload);
      }
      toast.success("Categoria guardada");
      setCategoryImageFile(null);
      setCategoryModalOpen(false);
      fetchBase();
    } catch (err) {
      toastError(err);
    }
  };

  const saveProduct = async () => {
    try {
      let imageUrl = productForm.imageUrl || "";
      if (productImageFile) {
        const formData = new FormData();
        formData.append("productImage", productImageFile);
        const { data } = await api.post("/orders/products/image", formData);
        imageUrl = data.imageUrl || imageUrl;
      }

      const payload = {
        ...productForm,
        imageUrl,
        connectionId: selectedConnectionId,
        categoryId: productForm.categoryId || null,
        options: (productForm.options || []).map(option => ({
          ...option,
          conditionalConfig: normalizeProductConditionalPayload(option.conditionalConfig),
          values: (option.values || []).filter(value => value.name),
        })),
        streamingConfig: {
          enabled: productForm.streamingConfig?.enabled === true,
          sendAccess: productForm.streamingConfig?.sendAccess !== false,
          items: productForm.streamingConfig?.enabled
            ? (productForm.streamingConfig?.items || [])
                .map(item => ({
                  platformId: toOptionalValue(item.platformId, NO_STREAMING_PLATFORM),
                  accessType: item.accessType === "account" ? "account" : "profile",
                  durationMode: item.durationMode === "option" ? "option" : "fixed",
                  durationDays: Number(item.durationDays || 30),
                  optionName: toOptionalValue(item.optionName, NO_STREAMING_OPTION),
                }))
                .filter(item => item.platformId)
            : [],
        },
        giftCardConfig: {
          enabled: productForm.giftCardConfig?.enabled === true,
          action: productForm.giftCardConfig?.action === "recharge" ? "recharge" : "create",
          amountMode:
            productForm.giftCardConfig?.amountMode === "fixed" ||
            productForm.giftCardConfig?.amountMode === "option"
              ? productForm.giftCardConfig.amountMode
              : "price",
          amount: Number(productForm.giftCardConfig?.amount || 0),
          optionName: toOptionalValue(productForm.giftCardConfig?.optionName, NO_GIFT_CARD_OPTION),
          sendAccess: productForm.giftCardConfig?.sendAccess !== false,
        },
        salesFlowConfig: {
          enabled: productForm.salesFlowConfig?.enabled === true,
          options: productForm.salesFlowConfig?.enabled
            ? (productForm.salesFlowConfig?.options || [])
                .map(option => ({
                  label: String(option.label || "").trim(),
                  sequenceId: toOptionalValue(option.sequenceId, NO_SEQUENCE),
                }))
                .filter(option => option.label && option.sequenceId)
            : [],
        },
      };
      if (productForm.id) {
        await api.put(`/orders/products/${productForm.id}`, payload);
      } else {
        await api.post("/orders/products", payload);
      }
      toast.success("Producto guardado");
      setProductImageFile(null);
      setProductModalOpen(false);
      fetchBase();
    } catch (err) {
      toastError(err);
    }
  };

  const saveUpsell = async () => {
    try {
      if (!upsellForm.offeredProductId || upsellForm.offeredProductId === NO_CATALOG_PRODUCT) {
        toast.error("Selecciona el producto que se va a ofrecer.");
        return;
      }

      const payload = {
        ...upsellForm,
        connectionId: selectedConnectionId,
        offeredProductId: upsellForm.offeredProductId,
        targetProductIds: upsellForm.targetProductIds || [],
        targetCategoryIds: upsellForm.targetCategoryIds || [],
        quantity: Number(upsellForm.quantity || 1),
        position: Number(upsellForm.position || 0),
      };
      if (upsellForm.id) {
        await api.put(`/orders/upsells/${upsellForm.id}`, payload);
      } else {
        await api.post("/orders/upsells", payload);
      }
      toast.success("Upsell guardado");
      setUpsellModalOpen(false);
      fetchBase();
    } catch (err) {
      toastError(err);
    }
  };

  const deleteResource = async (type, item) => {
    try {
      if (type === "order") await api.delete(`/orders/${item.id}`);
      if (type === "method") await api.delete(`/orders/payment-methods/${item.id}`);
      if (type === "shippingMethod") await api.delete(`/orders/shipping-methods/${item.id}`);
      if (type === "status") await api.delete(`/orders/statuses/${item.id}`);
      if (type === "category") await api.delete(`/orders/product-categories/${item.id}`);
      if (type === "product") await api.delete(`/orders/products/${item.id}`);
      if (type === "upsell") await api.delete(`/orders/upsells/${item.id}`);
      if (type === "giftCard") await api.delete(`/orders/gift-cards/${item.id}`);
      toast.success("Registro eliminado");
      setDeletingOrder(null);
      setDeletingMethod(null);
      setDeletingShippingMethod(null);
      setDeletingStatus(null);
      setDeletingCategory(null);
      setDeletingProduct(null);
      setDeletingUpsell(null);
      setDeletingGiftCard(null);
      if (type === "order") {
        setSelectedOrderIds(prevState =>
          prevState.filter(orderId => String(orderId) !== String(item.id))
        );
      }
      fetchBase();
      fetchOrders();
    } catch (err) {
      toastError(err);
    }
  };

  const allOrdersSelected =
    filteredOrders.length > 0 &&
    filteredOrders.every(order => selectedOrderIds.includes(String(order.id)));
  const selectedOrderCount = selectedOrderIds.length;

  const toggleOrderSelection = orderId => {
    const normalizedId = String(orderId);
    setSelectedOrderIds(prevState =>
      prevState.includes(normalizedId)
        ? prevState.filter(id => id !== normalizedId)
        : [...prevState, normalizedId]
    );
  };

  const toggleAllOrdersSelection = () => {
    if (allOrdersSelected) {
      setSelectedOrderIds([]);
      return;
    }

    setSelectedOrderIds(filteredOrders.map(order => String(order.id)));
  };

  const handleDeleteSelectedOrders = async () => {
    try {
      await Promise.all(
        selectedOrderIds.map(orderId => api.delete(`/orders/${orderId}`))
      );
      setSelectedOrderIds([]);
      toast.success("Pedidos eliminados");
      fetchOrders();
    } catch (err) {
      toastError(err);
    } finally {
      setBulkDeleteOpen(false);
    }
  };

  const addOrderItem = () => {
    setOrderForm(prev => ({
      ...prev,
      items: [
        ...(prev.items || []),
        { ...emptyOrderItem },
      ],
    }));
  };

  const updateOrderItem = (index, patch) => {
    const items = [...(orderForm.items || [])];
    items[index] = { ...items[index], ...patch };
    setOrderForm({ ...orderForm, items });
  };

  const addCheckoutField = () => {
    setOrderForm(prev => ({
      ...prev,
      checkoutFields: [...(prev.checkoutFields || []), { name: "", value: "", type: "text", required: false, confirmValue: true }],
    }));
  };

  const addSettingsCheckoutField = () => {
    setSettingsForm(prev => ({
      ...prev,
      checkoutFields: [...(prev.checkoutFields || []), { name: "", value: "", type: "text", required: true, confirmValue: true, options: [], conditionalConfig: emptyCheckoutConditionalConfig() }],
    }));
  };

  const updateProductOption = (index, patch) => {
    const options = [...(productForm.options || [])];
    options[index] = { ...options[index], ...patch };
    setProductForm({ ...productForm, options });
  };

  const updateProductOptionValue = (optionIndex, valueIndex, patch) => {
    const options = [...(productForm.options || [])];
    const values = [...(options[optionIndex].values || [])];
    values[valueIndex] = { ...values[valueIndex], ...patch };
    options[optionIndex] = { ...options[optionIndex], values };
    setProductForm({ ...productForm, options });
  };

  const renderProductOptionConditionalControls = (option, optionIndex) => {
    const conditionalConfig = normalizeProductConditionalPayload(option.conditionalConfig);
    const sourceOptions = (productForm.options || [])
      .filter((sourceOption, sourceIndex) => sourceIndex < optionIndex && sourceOption.name)
      .map(sourceOption => ({
        name: sourceOption.name,
        values: sourceOption.values || [],
      }));
    const selectedSource = sourceOptions.find(
      sourceOption => sourceOption.name === conditionalConfig.sourceOptionName
    );
    const sourceValues = selectedSource?.values || [];

    return (
      <div className={classes.conditionalRow}>
        <div className={classes.conditionalHeader}>
          <Checkbox
            checked={conditionalConfig.enabled === true}
            onChange={event =>
              updateProductOption(optionIndex, {
                conditionalConfig: {
                  ...conditionalConfig,
                  enabled: event.target.checked,
                },
              })
            }
            color="primary"
          />
          <span>Campo condicional</span>
        </div>
        {conditionalConfig.enabled === true && (
          <div className={classes.conditionalFields}>
            <FormControl variant="outlined">
              <InputLabel>Mostrar cuando la opcion</InputLabel>
              <Select
                label="Mostrar cuando la opcion"
                value={conditionalConfig.sourceOptionName || NO_PRODUCT_OPTION_CONDITION}
                onChange={event =>
                  updateProductOption(optionIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      sourceOptionName:
                        event.target.value === NO_PRODUCT_OPTION_CONDITION
                          ? ""
                          : event.target.value,
                      value: "",
                    },
                  })
                }
              >
                <MenuItem value={NO_PRODUCT_OPTION_CONDITION}>Seleccionar opcion</MenuItem>
                {sourceOptions.map(sourceOption => (
                  <MenuItem key={sourceOption.name} value={sourceOption.name}>
                    {sourceOption.name}
                  </MenuItem>
                ))}
                {!sourceOptions.length && (
                  <MenuItem value="" disabled>
                    Sin opciones anteriores
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControl variant="outlined">
              <InputLabel>Operador</InputLabel>
              <Select
                label="Operador"
                value={conditionalConfig.operator || "equals"}
                onChange={event =>
                  updateProductOption(optionIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      operator: event.target.value,
                    },
                  })
                }
              >
                {conditionOperators.map(operator => (
                  <MenuItem key={operator.value} value={operator.value}>
                    {operator.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {sourceValues.length ? (
              <FormControl variant="outlined">
                <InputLabel>Valor</InputLabel>
                <Select
                  label="Valor"
                  value={conditionalConfig.value || NO_PRODUCT_OPTION_CONDITION}
                  onChange={event =>
                    updateProductOption(optionIndex, {
                      conditionalConfig: {
                        ...conditionalConfig,
                        value:
                          event.target.value === NO_PRODUCT_OPTION_CONDITION
                            ? ""
                            : event.target.value,
                      },
                    })
                  }
                >
                  <MenuItem value={NO_PRODUCT_OPTION_CONDITION}>Seleccionar valor</MenuItem>
                  {sourceValues
                    .filter(value => value.name)
                    .map((value, valueIndex) => (
                      <MenuItem key={`${valueIndex}-${value.name}`} value={value.name}>
                        {value.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                variant="outlined"
                label="Valor"
                value={conditionalConfig.value || ""}
                onChange={event =>
                  updateProductOption(optionIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      value: event.target.value,
                    },
                  })
                }
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const updateSettingsCheckoutField = (index, patch) => {
    const checkoutFields = normalizeCheckoutFields(settingsForm.checkoutFields);
    checkoutFields[index] = { ...checkoutFields[index], ...patch };
    setSettingsForm({ ...settingsForm, checkoutFields });
  };

  const updateSettingsCheckoutOption = (fieldIndex, optionIndex, value) => {
    const checkoutFields = normalizeCheckoutFields(settingsForm.checkoutFields);
    const options = [...(checkoutFields[fieldIndex].options || [])];
    options[optionIndex] = value;
    checkoutFields[fieldIndex] = { ...checkoutFields[fieldIndex], options };
    setSettingsForm({ ...settingsForm, checkoutFields });
  };

  const renderCheckoutConditionalControls = (field, fieldIndex) => {
    const conditionalConfig = normalizeCheckoutConditionalPayload(field.conditionalConfig);
    const sourceFields = normalizeCheckoutFields(settingsForm.checkoutFields)
      .filter((sourceField, sourceIndex) => sourceIndex < fieldIndex && sourceField.name)
      .map(sourceField => ({
        name: sourceField.name,
        options: sourceField.options || [],
      }));
    const selectedSource = sourceFields.find(
      sourceField => sourceField.name === conditionalConfig.sourceFieldName
    );
    const sourceOptions = (selectedSource?.options || []).filter(Boolean);

    return (
      <div className={classes.conditionalRow}>
        <div className={classes.conditionalHeader}>
          <Checkbox
            checked={conditionalConfig.enabled === true}
            onChange={event =>
              updateSettingsCheckoutField(fieldIndex, {
                conditionalConfig: {
                  ...conditionalConfig,
                  enabled: event.target.checked,
                },
              })
            }
            color="primary"
          />
          <span>Campo condicional</span>
        </div>
        {conditionalConfig.enabled === true && (
          <div className={classes.conditionalFields}>
            <FormControl variant="outlined">
              <InputLabel>Mostrar cuando el campo</InputLabel>
              <Select
                label="Mostrar cuando el campo"
                value={conditionalConfig.sourceFieldName || NO_PRODUCT_OPTION_CONDITION}
                onChange={event =>
                  updateSettingsCheckoutField(fieldIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      sourceFieldName:
                        event.target.value === NO_PRODUCT_OPTION_CONDITION
                          ? ""
                          : event.target.value,
                      value: "",
                    },
                  })
                }
              >
                <MenuItem value={NO_PRODUCT_OPTION_CONDITION}>Seleccionar campo</MenuItem>
                {sourceFields.map(sourceField => (
                  <MenuItem key={sourceField.name} value={sourceField.name}>
                    {sourceField.name}
                  </MenuItem>
                ))}
                {!sourceFields.length && (
                  <MenuItem value="" disabled>
                    Sin campos anteriores
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControl variant="outlined">
              <InputLabel>Operador</InputLabel>
              <Select
                label="Operador"
                value={conditionalConfig.operator || "equals"}
                onChange={event =>
                  updateSettingsCheckoutField(fieldIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      operator: event.target.value,
                    },
                  })
                }
              >
                {conditionOperators.map(operator => (
                  <MenuItem key={operator.value} value={operator.value}>
                    {operator.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {sourceOptions.length ? (
              <FormControl variant="outlined">
                <InputLabel>Valor</InputLabel>
                <Select
                  label="Valor"
                  value={conditionalConfig.value || NO_PRODUCT_OPTION_CONDITION}
                  onChange={event =>
                    updateSettingsCheckoutField(fieldIndex, {
                      conditionalConfig: {
                        ...conditionalConfig,
                        value:
                          event.target.value === NO_PRODUCT_OPTION_CONDITION
                            ? ""
                            : event.target.value,
                      },
                    })
                  }
                >
                  <MenuItem value={NO_PRODUCT_OPTION_CONDITION}>Seleccionar valor</MenuItem>
                  {sourceOptions.map((option, optionIndex) => (
                    <MenuItem key={`${optionIndex}-${option}`} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                variant="outlined"
                label="Valor"
                value={conditionalConfig.value || ""}
                onChange={event =>
                  updateSettingsCheckoutField(fieldIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      value: event.target.value,
                    },
                  })
                }
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const updateShippingField = (index, patch) => {
    const fields = normalizeShippingFields(shippingMethodForm.fields);
    fields[index] = { ...fields[index], ...patch };
    setShippingMethodForm({ ...shippingMethodForm, fields });
  };

  const updateShippingFieldOption = (fieldIndex, optionIndex, patch) => {
    const fields = normalizeShippingFields(shippingMethodForm.fields);
    const options = normalizeShippingFieldOptions(fields[fieldIndex].options, true);
    options[optionIndex] = { ...options[optionIndex], ...patch };
    fields[fieldIndex] = { ...fields[fieldIndex], options };
    setShippingMethodForm({ ...shippingMethodForm, fields });
  };

  const renderShippingFieldConditionalControls = (field, fieldIndex) => {
    const conditionalConfig = normalizeCheckoutConditionalPayload(field.conditionalConfig);
    const sourceFields = normalizeShippingFields(shippingMethodForm.fields)
      .slice(0, fieldIndex)
      .filter(sourceField => sourceField.name)
      .map(sourceField => ({
        name: sourceField.name,
        options: normalizeShippingFieldOptions(sourceField.options),
      }));
    const selectedSource = sourceFields.find(
      sourceField => sourceField.name === conditionalConfig.sourceFieldName
    );
    const sourceOptions = (selectedSource?.options || []).filter(option => option.name);

    return (
      <div className={classes.conditionalRow}>
        <div className={classes.conditionalHeader}>
          <Checkbox
            checked={conditionalConfig.enabled === true}
            onChange={event =>
              updateShippingField(fieldIndex, {
                conditionalConfig: {
                  ...conditionalConfig,
                  enabled: event.target.checked,
                },
              })
            }
            color="primary"
          />
          <span>Campo condicional</span>
        </div>
        {conditionalConfig.enabled === true && (
          <div className={classes.conditionalFields}>
            <FormControl variant="outlined">
              <InputLabel>Aplicar cuando el campo</InputLabel>
              <Select
                label="Aplicar cuando el campo"
                value={conditionalConfig.sourceFieldName || NO_SHIPPING_FIELD_CONDITION}
                onChange={event =>
                  updateShippingField(fieldIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      sourceFieldName:
                        event.target.value === NO_SHIPPING_FIELD_CONDITION
                          ? ""
                          : event.target.value,
                      value: "",
                    },
                  })
                }
              >
                <MenuItem value={NO_SHIPPING_FIELD_CONDITION}>Seleccionar campo</MenuItem>
                {sourceFields.map(sourceField => (
                  <MenuItem key={sourceField.name} value={sourceField.name}>
                    {sourceField.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined">
              <InputLabel>Operador</InputLabel>
              <Select
                label="Operador"
                value={conditionalConfig.operator || "equals"}
                onChange={event =>
                  updateShippingField(fieldIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      operator: event.target.value,
                    },
                  })
                }
              >
                {conditionOperators.map(operator => (
                  <MenuItem key={operator.value} value={operator.value}>
                    {operator.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {sourceOptions.length ? (
              <FormControl variant="outlined">
                <InputLabel>Valor</InputLabel>
                <Select
                  label="Valor"
                  value={conditionalConfig.value || NO_SHIPPING_FIELD_CONDITION}
                  onChange={event =>
                    updateShippingField(fieldIndex, {
                      conditionalConfig: {
                        ...conditionalConfig,
                        value:
                          event.target.value === NO_SHIPPING_FIELD_CONDITION
                            ? ""
                            : event.target.value,
                      },
                    })
                  }
                >
                  <MenuItem value={NO_SHIPPING_FIELD_CONDITION}>Seleccionar valor</MenuItem>
                  {sourceOptions.map((option, optionIndex) => (
                    <MenuItem key={`${optionIndex}-${option.name}`} value={option.name}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                variant="outlined"
                label="Valor"
                value={conditionalConfig.value || ""}
                onChange={event =>
                  updateShippingField(fieldIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      value: event.target.value,
                    },
                  })
                }
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const updateStreamingConfig = patch => {
    setProductForm({
      ...productForm,
      streamingConfig: {
        ...emptyProduct.streamingConfig,
        ...(productForm.streamingConfig || {}),
        ...patch,
      },
    });
  };

  const updateStreamingConfigItem = (index, patch) => {
    const currentConfig = {
      ...emptyProduct.streamingConfig,
      ...(productForm.streamingConfig || {}),
    };
    const items = (currentConfig.items?.length ? currentConfig.items : [emptyStreamingConfigItem()]).map(
      (item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)
    );
    updateStreamingConfig({ items });
  };

  const updateGiftCardConfig = patch => {
    setProductForm({
      ...productForm,
      giftCardConfig: {
        ...emptyGiftCardConfig,
        ...(productForm.giftCardConfig || {}),
        ...patch,
      },
    });
  };

  const updateSalesFlowConfig = patch => {
    setProductForm({
      ...productForm,
      salesFlowConfig: {
        ...emptySalesFlowConfig,
        ...(productForm.salesFlowConfig || {}),
        ...patch,
      },
    });
  };

  const updateSalesFlowOption = (index, patch) => {
    const currentConfig = {
      ...emptySalesFlowConfig,
      ...(productForm.salesFlowConfig || {}),
    };
    const options = (currentConfig.options?.length
      ? currentConfig.options
      : [emptySalesFlowOption()]
    ).map((option, optionIndex) =>
      optionIndex === index ? { ...option, ...patch } : option
    );
    updateSalesFlowConfig({ options });
  };

  const money = value =>
    `${orderSettings.currencySymbol || ""}${formatMoney(value, orderSettings)}`;

  const renderStatusChip = key => {
    const status = statusByKey[key];
    return (
      <Chip
        size="small"
        label={status?.name || key}
        style={{ backgroundColor: status?.color || "#EEF2F7" }}
      />
    );
  };

  const renderOrderActions = order => (
    <>
      <Tooltip title="Editar">
        <IconButton size="small" onClick={() => openOrderModal(order)}>
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Eliminar">
        <IconButton size="small" onClick={() => setDeletingOrder(order)}>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  const renderOrderRow = order => (
    <TableRow key={order.id} hover>
      <TableCell padding="checkbox">
        <Checkbox
          size="small"
          color="primary"
          checked={selectedOrderIds.includes(String(order.id))}
          onChange={() => toggleOrderSelection(order.id)}
          inputProps={{ "aria-label": `Seleccionar ${order.number}` }}
        />
      </TableCell>
      <TableCell>{order.number}</TableCell>
      <TableCell>
        <Typography
          variant="body2"
          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
        >
          {privacyCensorSettings.contactNames
            ? "Contacto privado"
            : order.customerName || order.contact?.name}
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
        >
          {privacyCensorSettings.contactNames
            ? "Telefono privado"
            : order.customerPhone || order.contact?.number}
        </Typography>
      </TableCell>
      <TableCell>{renderStatusChip(order.status)}</TableCell>
      <TableCell>{paymentStatuses[order.paymentStatus] || order.paymentStatus}</TableCell>
      <TableCell>{order.paymentMethod?.name || "-"}</TableCell>
      <TableCell align="right">{money(order.total)}</TableCell>
      <TableCell align="right">{renderOrderActions(order)}</TableCell>
    </TableRow>
  );

  const renderMobileOrderCard = order => (
    <div className={classes.mobileCard} key={order.id}>
      <div className={classes.mobileCardHeader}>
        <Checkbox
          size="small"
          color="primary"
          checked={selectedOrderIds.includes(String(order.id))}
          onChange={() => toggleOrderSelection(order.id)}
          inputProps={{ "aria-label": `Seleccionar ${order.number}` }}
        />
        <div>
          <Typography className={classes.mobileOrderNumber}>
            {order.number}
          </Typography>
          <Typography
            className={
              privacyCensorSettings.contactNames
                ? classes.censoredText
                : classes.mobileOrderCustomer
            }
          >
            {privacyCensorSettings.contactNames
              ? "Contacto privado"
              : order.customerName || order.contact?.name || "-"}
          </Typography>
          <Typography
            className={
              privacyCensorSettings.contactNames
                ? classes.censoredText
                : classes.mobileOrderCustomer
            }
          >
            {privacyCensorSettings.contactNames
              ? "Telefono privado"
              : order.customerPhone || order.contact?.number || "-"}
          </Typography>
        </div>
        <div className={classes.mobileActions}>
          {renderOrderActions(order)}
        </div>
      </div>

      <Divider />

      <div className={classes.mobileCardGrid}>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Estado</span>
          <div className={classes.mobileValue}>{renderStatusChip(order.status)}</div>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Pago</span>
          <Typography variant="body2" className={classes.mobileValue}>
            {paymentStatuses[order.paymentStatus] || order.paymentStatus}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Metodo</span>
          <Typography variant="body2" className={classes.mobileValue}>
            {order.paymentMethod?.name || "-"}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Total</span>
          <Typography variant="body2" className={classes.mobileValue}>
            {money(order.total)}
          </Typography>
        </div>
      </div>
    </div>
  );

  const renderOrders = () => (
    <>
      <div className={classes.toolbar}>
        <div className={classes.filters}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={statusFilter || ALL_ORDER_STATUSES_FILTER}
              label="Estado"
              onChange={event =>
                setStatusFilter(
                  event.target.value === ALL_ORDER_STATUSES_FILTER ? "" : event.target.value
                )
              }
            >
              <MenuItem value={ALL_ORDER_STATUSES_FILTER}>Todos</MenuItem>
              {statuses.map(status => (
                <MenuItem key={status.id} value={status.key}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 190 }}>
            <InputLabel>Pago</InputLabel>
            <Select
              value={paymentFilter || ALL_PAYMENT_STATUSES_FILTER}
              label="Pago"
              onChange={event =>
                setPaymentFilter(
                  event.target.value === ALL_PAYMENT_STATUSES_FILTER ? "" : event.target.value
                )
              }
            >
              <MenuItem value={ALL_PAYMENT_STATUSES_FILTER}>Todos</MenuItem>
              {Object.entries(paymentStatuses).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Buscar"
            value={orderSearch}
            onChange={event => setOrderSearch(event.target.value)}
            variant="outlined"
            size="small"
            style={{ minWidth: 260 }}
          />
        </div>
      </div>
      {filteredOrders.length > 0 && (
        <div className={classes.bulkActions}>
          <Checkbox
            size="small"
            color="primary"
            checked={allOrdersSelected}
            indeterminate={selectedOrderCount > 0 && !allOrdersSelected}
            onChange={toggleAllOrdersSelection}
            inputProps={{ "aria-label": "Seleccionar pedidos" }}
          />
          <Typography variant="body2" className={classes.bulkActionsText}>
            {selectedOrderCount
              ? `${selectedOrderCount} seleccionado${
                  selectedOrderCount === 1 ? "" : "s"
                }`
              : "Seleccionar pedidos"}
          </Typography>
          {selectedOrderCount > 0 && (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedOrderIds([])}
              >
                Limpiar
              </Button>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<DeleteOutline />}
                onClick={() => setBulkDeleteOpen(true)}
              >
                Eliminar
              </Button>
            </>
          )}
        </div>
      )}

      <div className={classes.mobileCards}>
        {filteredOrders.map(renderMobileOrderCard)}
      </div>

      <div className={classes.tableWrapper}>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Pedido</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Pago</TableCell>
              <TableCell>Metodo</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{filteredOrders.map(renderOrderRow)}</TableBody>
        </Table>
      </div>
    </>
  );

  const renderGiftCardMobileCard = card => (
    <div className={classes.mobileCard} key={card.id}>
      <div className={classes.mobileCardHeader}>
        <div>
          <Typography className={classes.mobileOrderNumber}>
            {card.code}
          </Typography>
          {card.notes ? (
            <Typography variant="caption" color="textSecondary">
              {card.notes}
            </Typography>
          ) : null}
        </div>
        <div className={classes.mobileActions}>
          <IconButton
            size="small"
            onClick={() => {
              setGiftCardForm({
                ...emptyGiftCard,
                ...card,
                expiresAt: card.expiresAt ? String(card.expiresAt).slice(0, 10) : "",
              });
              setGiftCardModalOpen(true);
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => setDeletingGiftCard(card)}>
            <DeleteOutline fontSize="small" />
          </IconButton>
        </div>
      </div>

      <Divider />

      <div className={classes.mobileCardGrid}>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Saldo inicial</span>
          <Typography variant="body2" className={classes.mobileValue}>
            {money(card.initialBalance)}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Disponible</span>
          <Typography variant="body2" className={classes.mobileValue}>
            {money(card.balance)}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Origen</span>
          <Typography variant="body2" className={classes.mobileValue}>
            {card.autoGenerated ? "Automatica" : "Manual"}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Estado</span>
          <Typography variant="body2" className={classes.mobileValue}>
            {card.isActive ? "Activa" : "Inactiva"}
          </Typography>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper className={classes.settingsCard} elevation={0}>
          <div className={classes.toolbar}>
            <div>
              <Typography variant="subtitle1">Plantilla de prueba</Typography>
              <Typography variant="caption" color="textSecondary">
                Restablece catalogo, metodos de pago, metodos de envio, estados y campos de checkout para probar el carrito del chatbot.
              </Typography>
            </div>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<Restore />}
              disabled={!selectedConnectionId || selectedWhatsappTab?.orphan || templateResetting}
              onClick={() => setTemplateResetOpen(true)}
            >
              Restablecer plantilla
            </Button>
          </div>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper className={classes.settingsCard} elevation={0}>
          <div className={classes.toolbar}>
            <Typography variant="subtitle1">Configuracion monetaria</Typography>
            <Button color="primary" variant="outlined" onClick={saveSettings}>
              Guardar configuracion
            </Button>
          </div>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Simbolo de moneda"
                value={settingsForm.currencySymbol}
                onChange={event =>
                  setSettingsForm({
                    ...settingsForm,
                    currencySymbol: event.target.value,
                  })
                }
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Decimales"
                type="number"
                value={settingsForm.decimalPlaces}
                onChange={event =>
                  setSettingsForm({
                    ...settingsForm,
                    decimalPlaces: Math.min(4, Math.max(0, Number(event.target.value))),
                  })
                }
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Estado al pagar</InputLabel>
                <Select
                  value={settingsForm.paidStatusKey || "no-paid-status"}
                  label="Estado al pagar"
                  onChange={event =>
                    setSettingsForm({
                      ...settingsForm,
                      paidStatusKey:
                        event.target.value === "no-paid-status" ? "" : event.target.value,
                    })
                  }
                >
                  <MenuItem value="no-paid-status">No cambiar estado</MenuItem>
                  {statuses.map(status => (
                    <MenuItem key={status.id} value={status.key}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Estado pago parcial</InputLabel>
                <Select
                  value={settingsForm.partiallyPaidStatusKey || "no-partial-status"}
                  label="Estado pago parcial"
                  onChange={event =>
                    setSettingsForm({
                      ...settingsForm,
                      partiallyPaidStatusKey:
                        event.target.value === "no-partial-status" ? "" : event.target.value,
                    })
                  }
                >
                  <MenuItem value="no-partial-status">No cambiar estado</MenuItem>
                  {statuses.map(status => (
                    <MenuItem key={status.id} value={status.key}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Metodo Gift Card</InputLabel>
                <Select
                  value={settingsForm.giftCardPaymentMethodId || NO_PAYMENT_METHOD}
                  label="Metodo Gift Card"
                  onChange={event =>
                    setSettingsForm({
                      ...settingsForm,
                      giftCardPaymentMethodId:
                        event.target.value === NO_PAYMENT_METHOD ? "" : event.target.value,
                    })
                  }
                >
                  <MenuItem value={NO_PAYMENT_METHOD}>Crear automatico</MenuItem>
                  {paymentMethods.map(method => (
                    <MenuItem key={method.id} value={method.id}>
                      {method.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper className={classes.settingsCard} elevation={0}>
          <div className={classes.toolbar}>
            <Typography variant="subtitle1">Metodos de envio</Typography>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<LocalShipping />}
              onClick={() => openCreateShippingMethod()}
              disabled={!selectedConnectionId || selectedWhatsappTab?.orphan}
            >
              Agregar
            </Button>
          </div>
          <Table size="small">
            <TableBody>
              {shippingMethods.map(method => (
                <TableRow key={method.id}>
                  <TableCell>
                    <Typography variant="body2">{method.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {[money(method.cost), method.isActive ? "Activo" : "Inactivo"]
                        .concat(method.requiresLocation ? ["Solicita ubicacion"] : [])
                        .concat((method.fields || []).length ? [`${method.fields.length} campos`] : [])
                        .filter(Boolean)
                        .join(" - ")}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => openEditShippingMethod(method.id)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeletingShippingMethod(method)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper className={classes.settingsCard} elevation={0}>
          <div className={classes.toolbar}>
            <Typography variant="subtitle1">Checkout</Typography>
            <div className={classes.settingsActions}>
              <Button
                color="primary"
                variant="outlined"
                startIcon={<Add />}
                onClick={addSettingsCheckoutField}
              >
                Agregar
              </Button>
              <Button color="primary" variant="outlined" onClick={saveSettings}>
                Guardar configuracion
              </Button>
            </div>
          </div>
          {normalizeCheckoutFields(settingsForm.checkoutFields).map((field, index) => (
            <div className={classes.productOptionBox} key={index}>
              <div className={classes.checkoutSettingsRow}>
                <div className={classes.checkoutSettingsFields}>
                  <TextField
                    label="Dato"
                    value={field.name || ""}
                    onChange={event => updateSettingsCheckoutField(index, { name: event.target.value })}
                    variant="outlined"
                    margin="dense"
                  />
                  <FormControl variant="outlined" margin="dense">
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      label="Tipo"
                      value={field.type || "text"}
                      onChange={event =>
                        updateSettingsCheckoutField(index, {
                          type: event.target.value,
                          options:
                            event.target.value === "select" || event.target.value === "multiple"
                              ? field.options || []
                              : [],
                        })
                      }
                    >
                      {checkoutFieldTypes.map(type => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className={classes.checkoutSettingsControls}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={field.required !== false}
                        onChange={event => updateSettingsCheckoutField(index, { required: event.target.checked })}
                      />
                    }
                    label="Requerido"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={field.confirmValue !== false}
                        onChange={event =>
                          updateSettingsCheckoutField(index, {
                            confirmValue: event.target.checked,
                          })
                        }
                      />
                    }
                    label="Confirmar dato"
                  />
                  <IconButton
                    className={classes.checkoutDeleteButton}
                    size="small"
                    onClick={() => setDeletingCheckoutFieldIndex(index)}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </div>
              </div>
              {renderCheckoutConditionalControls(field, index)}
              {(field.type === "select" || field.type === "multiple") && (
                <div className={classes.checkoutOptions}>
                  {(field.options || []).map((option, optionIndex) => (
                    <div className={classes.checkoutOptionRow} key={`${index}-${optionIndex}`}>
                      <TextField
                        label="Opcion"
                        value={option}
                        onChange={event => updateSettingsCheckoutOption(index, optionIndex, event.target.value)}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      />
                      <IconButton
                        size="small"
                        onClick={() =>
                          updateSettingsCheckoutField(index, {
                            options: (field.options || []).filter((_, itemIndex) => itemIndex !== optionIndex),
                          })
                        }
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
                  <Button
                    color="primary"
                    onClick={() =>
                      updateSettingsCheckoutField(index, {
                        options: [...(field.options || []), ""],
                      })
                    }
                  >
                    Agregar opcion
                  </Button>
                </div>
              )}
            </div>
          ))}
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Secuencia al confirmar pedido</InputLabel>
                <Select
                  value={settingsForm.orderConfirmationSequenceId || NO_SEQUENCE}
                  label="Secuencia al confirmar pedido"
                  onChange={event => {
                    if (event.target.value === "__create__") {
                      openCreateSequence(sequenceId =>
                        setSettingsForm(prev => ({
                          ...prev,
                          orderConfirmationSequenceId: sequenceId,
                        }))
                      );
                      return;
                    }
                    setSettingsForm(prev => ({
                      ...prev,
                      orderConfirmationSequenceId:
                        event.target.value === NO_SEQUENCE
                          ? NO_SEQUENCE
                          : event.target.value,
                    }));
                  }}
                >
                  <MenuItem value={NO_SEQUENCE}>Sin secuencia</MenuItem>
                  <MenuItem value="__create__">Crear secuencia...</MenuItem>
                  {sequences
                    .filter(sequence =>
                      String(sequence.connectionId || "") === String(selectedConnectionId || "") ||
                      String(sequence.id) === String(settingsForm.orderConfirmationSequenceId || "")
                    )
                    .map(sequence => (
                      <MenuItem key={sequence.id} value={String(sequence.id)}>
                        {formatSequenceOption(sequence)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <div className={classes.paymentMethodActions}>
                <Button
                  color="primary"
                  disabled={
                    !settingsForm.orderConfirmationSequenceId ||
                    settingsForm.orderConfirmationSequenceId === NO_SEQUENCE
                  }
                  onClick={() =>
                    openEditSequence(
                      settingsForm.orderConfirmationSequenceId,
                      sequenceId =>
                        setSettingsForm(prev => ({
                          ...prev,
                          orderConfirmationSequenceId: sequenceId,
                        }))
                    )
                  }
                >
                  Editar secuencia
                </Button>
              </div>
            </Grid>
          </Grid>
          <Typography variant="caption" color="textSecondary">
            Estos campos se cargan automaticamente en los pedidos nuevos.
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper className={classes.settingsCard} elevation={0}>
          <div className={classes.toolbar}>
            <Typography variant="subtitle1">Metodos de pago</Typography>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<Payment />}
              onClick={() => openCreatePaymentMethod()}
              disabled={!selectedConnectionId || selectedWhatsappTab?.orphan}
            >
              Agregar
            </Button>
          </div>
          <Table size="small">
            <TableBody>
              {paymentMethods.map(method => (
                <TableRow key={method.id}>
                  <TableCell>
                    <Typography variant="body2">{method.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {method.requiresProof ? "Requiere comprobante" : "Sin comprobante obligatorio"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => openEditPaymentMethod(method.id)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeletingMethod(method)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper className={classes.settingsCard} elevation={0}>
          <div className={classes.toolbar}>
            <Typography variant="subtitle1">Estados de pedido</Typography>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => {
                setStatusForm({ ...emptyStatus, position: statuses.length });
                setStatusModalOpen(true);
              }}
            >
              Agregar
            </Button>
          </div>
          <Table size="small">
            <TableBody>
              {statuses.map(status => (
                <TableRow key={status.id}>
                  <TableCell>
                    <span className={classes.statusDot} style={{ background: status.color }} />
                    {status.name}
                    {status.triggersStreamingFulfillment && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        Activa entrega y asignacion de Gift Card y Streaming
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{status.key}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setStatusForm({ ...emptyStatus, ...status });
                        setStatusModalOpen(true);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeletingStatus(status)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderCatalog = () => (
    <Grid container spacing={2} style={{ marginTop: 8 }}>
      <Grid item xs={12} md={5}>
        <Paper className={classes.settingsCard} elevation={0}>
          <div className={classes.toolbar}>
            <Typography variant="subtitle1">Categorias</Typography>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => {
                setCategoryImageFile(null);
                setCategoryForm({ ...emptyCategory, position: productCategories.length });
                setCategoryModalOpen(true);
              }}
            >
              Agregar
            </Button>
          </div>
          <Table size="small">
            <TableBody>
              {productCategories.map(category => (
                <TableRow key={category.id}>
                  <TableCell>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {category.imageUrl ? (
                        <img
                          src={buildFileHref(category.imageUrl)}
                          alt={category.name}
                          style={{
                            width: 42,
                            height: 42,
                            borderRadius: 6,
                            objectFit: "cover",
                            border: "1px solid rgba(0,0,0,.08)"
                          }}
                        />
                      ) : null}
                      <div>
                        <Typography variant="body2">{category.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {category.isActive ? "Activa" : "Inactiva"}
                        </Typography>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setCategoryImageFile(null);
                        setCategoryForm({ ...emptyCategory, ...category });
                        setCategoryModalOpen(true);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeletingCategory(category)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
      <Grid item xs={12} md={7}>
        <Paper className={classes.settingsCard} elevation={0}>
          <div className={classes.toolbar}>
            <Typography variant="subtitle1">Productos</Typography>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => {
                setProductImageFile(null);
                setProductForm({
                  ...emptyProduct,
                  position: products.length,
                  options: normalizeProductOptionsForForm([]),
                  salesFlowConfig: normalizeSalesFlowConfigForForm(emptySalesFlowConfig),
                });
                setProductModalOpen(true);
              }}
            >
              Agregar
            </Button>
          </div>
          <Table size="small">
            <TableBody>
              {products.map(product => (
                <TableRow key={product.id}>
                  <TableCell>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {product.imageUrl ? (
                        <img
                          src={buildFileHref(product.imageUrl)}
                          alt={product.name}
                          style={{
                            width: 42,
                            height: 42,
                            borderRadius: 6,
                            objectFit: "cover",
                            border: "1px solid rgba(0,0,0,.08)"
                          }}
                        />
                      ) : null}
                      <div>
                        <Typography variant="body2">{product.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {[product.categoryName, money(product.price), product.isActive ? "Activo" : "Inactivo"].filter(Boolean).join(" · ")}
                        </Typography>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setProductImageFile(null);
                        setProductForm({
                          ...emptyProduct,
                          ...product,
                          options: normalizeProductOptionsForForm(product.options),
                          salesFlowConfig: normalizeSalesFlowConfigForForm(product.salesFlowConfig),
                        });
                        setProductModalOpen(true);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeletingProduct(product)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.settingsCard} elevation={0}>
          <div className={classes.toolbar}>
            <div>
              <Typography variant="subtitle1">Upsells</Typography>
              <Typography variant="caption" color="textSecondary">
                Se muestran despues de confirmar el carrito y antes del checkout.
              </Typography>
            </div>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => {
                setUpsellForm({
                  ...emptyUpsell,
                  offeredProductId: products[0]?.id || NO_CATALOG_PRODUCT,
                  position: upsells.length,
                });
                setUpsellModalOpen(true);
              }}
              disabled={!products.length}
            >
              Agregar
            </Button>
          </div>
          <Table size="small">
            <TableBody>
              {upsells.map(upsell => {
                const targetProductNames = (upsell.targetProductIds || [])
                  .map(productId => products.find(product => String(product.id) === String(productId))?.name)
                  .filter(Boolean);
                const targetCategoryNames = (upsell.targetCategoryIds || [])
                  .map(categoryId => productCategories.find(category => String(category.id) === String(categoryId))?.name)
                  .filter(Boolean);
                const appliesTo = [
                  targetProductNames.length ? `Productos: ${targetProductNames.join(", ")}` : "",
                  targetCategoryNames.length ? `Categorias: ${targetCategoryNames.join(", ")}` : "",
                ].filter(Boolean).join(" | ") || "Todo el carrito";

                return (
                  <TableRow key={upsell.id}>
                    <TableCell>
                      <Typography variant="body2">{upsell.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {[`Ofrece: ${upsell.offeredProduct?.name || "-"}`, appliesTo, upsell.isActive ? "Activo" : "Inactivo"].filter(Boolean).join(" · ")}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setUpsellForm({
                            ...emptyUpsell,
                            ...upsell,
                            offeredProductId: upsell.offeredProductId || upsell.offeredProduct?.id || NO_CATALOG_PRODUCT,
                            targetProductIds: upsell.targetProductIds || [],
                            targetCategoryIds: upsell.targetCategoryIds || [],
                          });
                          setUpsellModalOpen(true);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => setDeletingUpsell(upsell)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderGiftCards = () => (
    <Grid container spacing={2} style={{ marginTop: 8 }}>
      <Grid item xs={12}>
        <Paper className={classes.settingsCard} elevation={0}>
          <div className={classes.toolbar}>
            <div>
              <Typography variant="subtitle1">Gift Cards - Tarjetas de regalo</Typography>
              <Typography variant="caption" color="textSecondary">
                Crea codigos con saldo para que los clientes puedan pagar pedidos.
              </Typography>
            </div>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => {
                setGiftCardForm({ ...emptyGiftCard });
                setGiftCardModalOpen(true);
              }}
              disabled={!selectedConnectionId || selectedWhatsappTab?.orphan}
            >
              Agregar
            </Button>
          </div>
          <div className={classes.mobileCards}>
            {giftCards.length ? (
              giftCards.map(renderGiftCardMobileCard)
            ) : (
              <Typography variant="body2" color="textSecondary">
                No hay tarjetas creadas.
              </Typography>
            )}
          </div>
          <div className={classes.giftCardTableWrapper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Codigo</TableCell>
                  <TableCell>Saldo inicial</TableCell>
                  <TableCell>Saldo disponible</TableCell>
                  <TableCell>Origen</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {giftCards.map(card => (
                  <TableRow key={card.id}>
                    <TableCell>
                      <Typography variant="body2">{card.code}</Typography>
                      {card.notes ? (
                        <Typography variant="caption" color="textSecondary">
                          {card.notes}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{money(card.initialBalance)}</TableCell>
                    <TableCell>{money(card.balance)}</TableCell>
                    <TableCell>{card.autoGenerated ? "Automatica" : "Manual"}</TableCell>
                    <TableCell>{card.isActive ? "Activa" : "Inactiva"}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setGiftCardForm({
                            ...emptyGiftCard,
                            ...card,
                            expiresAt: card.expiresAt ? String(card.expiresAt).slice(0, 10) : "",
                          });
                          setGiftCardModalOpen(true);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => setDeletingGiftCard(card)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!giftCards.length && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="textSecondary">
                        No hay tarjetas creadas.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Paper>
      </Grid>
    </Grid>
  );

  const checkoutFieldsWithIndexes = (orderForm.checkoutFields || []).map(
    (field, index) => ({ field, index })
  );
  const deliveryLocationField = checkoutFieldsWithIndexes.find(({ field }) =>
    isDeliveryLocationField(field)
  )?.field;
  const visibleCheckoutFields = checkoutFieldsWithIndexes.filter(
    ({ field }) => !isDeliveryLocationField(field)
  );
  const proofUrl = proofRemoved ? "" : orderForm.paymentProofUrl || "";
  const proofName = proofFile?.name || (!proofRemoved ? orderForm.paymentProofName : "") || "";
  const proofHref = buildFileHref(proofUrl);
  const proofPreviewUrl = proofFilePreviewUrl || proofUrl;
  const proofPreviewHref = proofFilePreviewUrl || proofHref;
  const proofFileInfo = {
    mimeType: proofFile?.type || orderForm.paymentProofMimeType || "",
    name: proofName,
    url: proofPreviewUrl,
  };
  const proofIsImage = isImageFile(proofFileInfo);
  const proofIsPdf = isPdfFile(proofFileInfo);
  const productImageSrc = productImagePreviewUrl || buildFileHref(productForm.imageUrl || "");
  const productImageName =
    productImageFile?.name || getFileNameFromUrl(productForm.imageUrl) || "";
  const categoryImageSrc = categoryImagePreviewUrl || buildFileHref(categoryForm.imageUrl || "");
  const categoryImageName =
    categoryImageFile?.name || getFileNameFromUrl(categoryForm.imageUrl) || "";
  const productOptionSummary = (productForm.options || [])
    .filter(option => option.name)
    .map(option => {
      const values = (option.values || [])
        .filter(value => value.name)
        .map(value =>
          Number(value.priceDelta || 0)
            ? `${value.name} (+${money(value.priceDelta)})`
            : value.name
        );
      return values.length ? `${option.name}: ${values.join(", ")}` : option.name;
    });
  const productMessageBody = [
    productForm.name || "Nombre del producto",
    productForm.description,
    productForm.price ? `Precio: ${money(productForm.price)}` : "",
    productOptionSummary.length ? `Opciones:\n${productOptionSummary.join("\n")}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <MainContainer>
      <MainHeader>
        <Title>Pedidos</Title>
        <MainHeaderButtonsWrapper>
          <Button
            color="primary"
            variant="contained"
            startIcon={<ShoppingCartOutlined />}
            onClick={() => openOrderModal()}
            disabled={!selectedConnectionId || selectedWhatsappTab?.orphan}
          >
            Nuevo pedido
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Tabs
          className={classes.tabsScroller}
          value={selectedConnectionId}
          onChange={(event, value) => setSelectedConnectionId(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {whatsappTabs.length ? (
            whatsappTabs.map(whatsapp => (
              <Tab
                key={whatsapp.value}
                value={whatsapp.value}
                label={whatsapp.name || whatsapp.number || `Conexion ${whatsapp.id}`}
              />
            ))
          ) : (
            <Tab value="" label="Sin conexiones" disabled />
          )}
        </Tabs>
        <Tabs
          className={classes.tabsScroller}
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="orders" label="Pedidos" />
          <Tab value="catalog" label="Catalogo" />
          <Tab value="giftCards" label="Gift Cards" />
          <Tab value="settings" label="Configuracion" />
        </Tabs>
        {tab === "orders" && renderOrders()}
        {tab === "catalog" && renderCatalog()}
        {tab === "giftCards" && renderGiftCards()}
        {tab === "settings" && renderSettings()}
      </Paper>

      <Dialog open={orderModalOpen} onClose={() => setOrderModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{orderForm.id ? `Pedido ${orderForm.number}` : "Nuevo pedido"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={contactOptions}
                value={selectedOrderContact}
                getOptionSelected={(option, value) =>
                  String(option.id) === String(value.id)
                }
                getOptionLabel={option =>
                  option
                    ? privacyCensorSettings.contactNames
                      ? "Contacto privado"
                      : `${option.name} - ${option.number}`
                    : ""
                }
                renderOption={renderContactOption}
                onInputChange={(_, value) => setContactSearch(value)}
                onChange={(_, value) => {
                  setSelectedOrderContact(value);
                  if (!value) {
                    setOrderForm(prev => ({
                      ...prev,
                      contactId: "",
                      customerName: "",
                      customerPhone: "",
                      customerEmail: "",
                    }));
                    return;
                  }
                  setOrderForm(prev => ({
                    ...prev,
                    contactId: value.id,
                    customerName: value.name || "",
                    customerPhone: value.number || "",
                    customerEmail: value.email || "",
                  }));
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Contacto"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Nombre"
                value={
                  privacyCensorSettings.contactNames
                    ? "Contacto privado"
                    : orderForm.customerName
                }
                variant="outlined"
                margin="dense"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Telefono"
                value={
                  privacyCensorSettings.contactNames
                    ? "Telefono privado"
                    : orderForm.customerPhone
                }
                variant="outlined"
                margin="dense"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Correo"
                value={
                  privacyCensorSettings.contactNames
                    ? "Correo privado"
                    : orderForm.customerEmail
                }
                variant="outlined"
                margin="dense"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select value={orderForm.status || statuses[0]?.key || ""} label="Estado" onChange={event => setOrderForm({ ...orderForm, status: event.target.value })}>
                  {statuses.map(status => <MenuItem key={status.id} value={status.key}>{status.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Estado de pago</InputLabel>
                <Select value={orderForm.paymentStatus || "pending"} label="Estado de pago" onChange={event => setOrderForm({ ...orderForm, paymentStatus: event.target.value })}>
                  {Object.entries(paymentStatuses).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Metodo de pago</InputLabel>
                <Select
                  value={orderForm.paymentMethodId || NO_PAYMENT_METHOD}
                  label="Metodo de pago"
                  onChange={event => {
                    if (event.target.value === "__create__") {
                      openCreatePaymentMethod("order");
                      return;
                    }

                    setOrderForm({
                      ...orderForm,
                      paymentMethodId: event.target.value,
                    });
                  }}
                >
                  <MenuItem value={NO_PAYMENT_METHOD}>Sin metodo</MenuItem>
                  <MenuItem value="__create__">Crear metodo...</MenuItem>
                  {paymentMethods.map(method => <MenuItem key={method.id} value={method.id}>{method.name}</MenuItem>)}
                </Select>
              </FormControl>
              <div className={classes.paymentMethodActions}>
                <Button
                  color="primary"
                  disabled={!orderForm.paymentMethodId || orderForm.paymentMethodId === NO_PAYMENT_METHOD}
                  onClick={() =>
                    openEditPaymentMethod(orderForm.paymentMethodId, "order")
                  }
                >
                  Editar metodo
                </Button>
              </div>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Metodo de envio</InputLabel>
                <Select
                  value={orderForm.shippingMethodId || NO_SHIPPING_METHOD}
                  label="Metodo de envio"
                  onChange={event => {
                    if (event.target.value === "__create__") {
                      openCreateShippingMethod("order");
                      return;
                    }

                    const method = shippingMethods.find(
                      entry => String(entry.id) === String(event.target.value)
                    );
                    setOrderForm({
                      ...orderForm,
                      shippingMethodId: event.target.value,
                      shipping:
                        event.target.value === NO_SHIPPING_METHOD
                          ? 0
                          : Number(method?.cost || 0),
                    });
                  }}
                >
                  <MenuItem value={NO_SHIPPING_METHOD}>Sin envio</MenuItem>
                  <MenuItem value="__create__">Crear metodo...</MenuItem>
                  {shippingMethods.map(method => (
                    <MenuItem key={method.id} value={method.id}>
                      {method.name}
                      {method.cost ? ` - ${money(method.cost)}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div className={classes.paymentMethodActions}>
                <Button
                  color="primary"
                  disabled={
                    !orderForm.shippingMethodId ||
                    orderForm.shippingMethodId === NO_SHIPPING_METHOD
                  }
                  onClick={() =>
                    openEditShippingMethod(orderForm.shippingMethodId, "order")
                  }
                >
                  Editar envio
                </Button>
              </div>
            </Grid>
          </Grid>

          <Typography variant="subtitle2" className={classes.sectionTitle}>Productos</Typography>
          {(orderForm.items || []).map((item, index) => (
            <div className={classes.itemRow} key={index}>
              <IconButton className={classes.itemDeleteButton} size="small" onClick={() => setOrderForm({ ...orderForm, items: orderForm.items.filter((_, itemIndex) => itemIndex !== index) })}>
                <DeleteOutline fontSize="small" />
              </IconButton>
              <FormControl className={classes.itemCatalogWideField} variant="outlined" margin="dense">
                <InputLabel>Producto</InputLabel>
                <Select
                  value={item.catalogProductId || NO_CATALOG_PRODUCT}
                  label="Producto"
                  onChange={event => {
                    if (event.target.value === NO_CATALOG_PRODUCT) {
                      updateOrderItem(index, {
                        catalogProductId: NO_CATALOG_PRODUCT,
                        productName: "",
                        catalogOptions: [],
                        optionSelections: {},
                        basePrice: 0,
                        variantName: NO_PRODUCT_VARIANT,
                        unitPrice: 0,
                      });
                      return;
                    }
                    const product = products.find(entry => String(entry.id) === String(event.target.value));
                    const nextItem = {
                      ...item,
                      catalogProductId: product?.id || NO_CATALOG_PRODUCT,
                      productName: product?.name || item.productName || "",
                      catalogOptions: product?.options || [],
                      optionSelections: {},
                      basePrice: Number(product?.price || 0),
                      variantName: NO_PRODUCT_VARIANT,
                    };
                    nextItem.unitPrice = calculateItemUnitPrice(nextItem);
                    updateOrderItem(index, {
                      catalogProductId: nextItem.catalogProductId,
                      productName: nextItem.productName,
                      catalogOptions: nextItem.catalogOptions,
                      optionSelections: nextItem.optionSelections,
                      basePrice: nextItem.basePrice,
                      variantName: nextItem.variantName,
                      unitPrice: nextItem.unitPrice,
                    });
                  }}
                >
                  <MenuItem value={NO_CATALOG_PRODUCT}>Producto manual</MenuItem>
                  {products.map(product => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name}
                      {product.price ? ` - ${money(product.price)}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                className={classes.itemQuantityField}
                label="Cant."
                type="number"
                value={item.quantity}
                onChange={event => updateOrderItem(index, { quantity: event.target.value })}
                variant="outlined"
                margin="dense"
              />
              <TextField
                className={classes.itemPriceField}
                label="Unitario"
                type="number"
                value={item.unitPrice}
                onChange={event =>
                  updateOrderItem(index, {
                    unitPrice: event.target.value,
                    basePrice:
                      item.catalogProductId && item.catalogProductId !== NO_CATALOG_PRODUCT
                        ? event.target.value
                        : item.basePrice,
                  })
                }
                variant="outlined"
                margin="dense"
                disabled={item.catalogProductId && item.catalogProductId !== NO_CATALOG_PRODUCT}
              />
              <div className={classes.itemTotalBox}>
                <Typography variant="caption" color="textSecondary">
                  Total
                </Typography>
                <Typography variant="subtitle2">
                  {money(Number(item.quantity || 0) * Number(item.unitPrice || 0))}
                </Typography>
              </div>
              {(!item.catalogProductId || item.catalogProductId === NO_CATALOG_PRODUCT) && (
                <div className={classes.itemOptionsGrid}>
                  <TextField
                    className={classes.itemOptionControl}
                    label="Nombre manual"
                    value={item.productName}
                    onChange={event =>
                      updateOrderItem(index, {
                        productName: event.target.value,
                        catalogProductId: NO_CATALOG_PRODUCT,
                        catalogOptions: [],
                        optionSelections: {},
                      })
                    }
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                  <TextField
                    className={classes.itemOptionControl}
                    label="Detalle / opciones"
                    value={item.variantName === NO_PRODUCT_VARIANT ? "" : item.variantName || ""}
                    onChange={event => updateOrderItem(index, { variantName: event.target.value })}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </div>
              )}
              {item.catalogProductId &&
                item.catalogProductId !== NO_CATALOG_PRODUCT &&
                Boolean((item.catalogOptions || []).length) && (
                  <div className={classes.itemOptionsGrid}>
                    {getVisibleProductOptions(
                      item.catalogOptions || [],
                      item.optionSelections || {}
                    ).map(option => {
                      const selectedValue = item.optionSelections?.[option.name];
                      const optionExtra = getOptionExtra(option, selectedValue);

                      return (
                        <div key={option.name}>
                          <FormControl
                            className={classes.itemOptionControl}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                          >
                            <InputLabel>{option.name}</InputLabel>
                            <Select
                              multiple={option.multiple === true}
                              value={
                                option.multiple
                                  ? normalizeSelectionArray(selectedValue)
                                  : selectedValue || NO_PRODUCT_VARIANT
                              }
                              label={option.name}
                              renderValue={selected =>
                                Array.isArray(selected)
                                  ? selected.length
                                    ? selected.join(", ")
                                    : option.required
                                    ? "Selecciona"
                                    : `Sin ${option.name}`
                                  : selected === NO_PRODUCT_VARIANT
                                  ? option.required
                                    ? "Selecciona"
                                    : `Sin ${option.name}`
                                  : selected
                              }
                              onChange={event => {
                                const rawSelections = {
                                  ...(item.optionSelections || {}),
                                  [option.name]:
                                    event.target.value === NO_PRODUCT_VARIANT
                                      ? ""
                                      : event.target.value,
                                };
                                const nextSelections = cleanHiddenProductSelections(
                                  item.catalogOptions || [],
                                  rawSelections
                                );
                                const nextItem = {
                                  ...item,
                                  optionSelections: nextSelections,
                                };
                                const unitPrice = calculateItemUnitPrice(nextItem);
                                updateOrderItem(index, {
                                  optionSelections: nextSelections,
                                  unitPrice,
                                  variantName: buildItemOptionText(nextItem) || NO_PRODUCT_VARIANT,
                                });
                              }}
                            >
                              {!option.required && !option.multiple && (
                                <MenuItem value={NO_PRODUCT_VARIANT}>
                                  Sin {option.name}
                                </MenuItem>
                              )}
                              {getOptionValues(option).map(value => (
                                <MenuItem key={value.name} value={value.name}>
                                  {option.multiple && (
                                    <Checkbox
                                      checked={normalizeSelectionArray(selectedValue).includes(
                                        value.name
                                      )}
                                    />
                                  )}
                                  <ListItemText
                                    primary={value.name}
                                    secondary={
                                      Number(value.priceDelta || 0)
                                        ? `+ ${money(value.priceDelta)}`
                                        : ""
                                    }
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Typography
                            className={classes.itemOptionPrice}
                            variant="caption"
                            color="textSecondary"
                          >
                            {optionExtra ? `Extra: ${money(optionExtra)}` : "Sin extra"}
                          </Typography>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          ))}
          <Button color="primary" startIcon={<Add />} onClick={addOrderItem}>Agregar producto</Button>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField label="Descuento" type="number" value={orderForm.discount} onChange={event => setOrderForm({ ...orderForm, discount: event.target.value })} variant="outlined" margin="dense" fullWidth />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField label="Impuesto" type="number" value={orderForm.tax} onChange={event => setOrderForm({ ...orderForm, tax: event.target.value })} variant="outlined" margin="dense" fullWidth />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Costo de envio"
                type="number"
                value={orderForm.shipping}
                onChange={event => setOrderForm({ ...orderForm, shipping: event.target.value })}
                variant="outlined"
                margin="dense"
                fullWidth
                disabled={
                  orderForm.shippingMethodId &&
                  orderForm.shippingMethodId !== NO_SHIPPING_METHOD
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="textSecondary">Total</Typography>
              <Typography variant="h6">{money(orderTotal)}</Typography>
            </Grid>
          </Grid>

          <Typography variant="subtitle2" className={classes.sectionTitle}>Detalles de facturacion</Typography>
          {visibleCheckoutFields.map(({ field, index }) => (
            <div className={classes.checkoutRow} key={index}>
              <TextField label="Campo" value={field.name} onChange={event => {
                const checkoutFields = [...orderForm.checkoutFields];
                checkoutFields[index] = { ...field, name: event.target.value };
                setOrderForm({ ...orderForm, checkoutFields });
              }} variant="outlined" margin="dense" />
              <TextField label="Valor" value={field.value} onChange={event => {
                const checkoutFields = [...orderForm.checkoutFields];
                checkoutFields[index] = { ...field, value: event.target.value };
                setOrderForm({ ...orderForm, checkoutFields });
              }} variant="outlined" margin="dense" multiline minRows={2} fullWidth />
              <IconButton className={classes.checkoutDeleteButton} size="small" onClick={() => setOrderForm({ ...orderForm, checkoutFields: orderForm.checkoutFields.filter((_, fieldIndex) => fieldIndex !== index) })}>
                <DeleteOutline fontSize="small" />
              </IconButton>
            </div>
          ))}
          <Button color="primary" startIcon={<Add />} onClick={addCheckoutField}>Nuevo campo</Button>

          <div className={classes.proofGrid}>
            <div className={classes.proofPanel}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>Ubicacion de entrega</Typography>
              <div className={classes.locationBox}>
                {deliveryLocationField?.value ? (
                  <LocationPreview
                    link={deliveryLocationField.value}
                    description={deliveryLocationField.value}
                  />
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Sin ubicacion
                  </Typography>
                )}
              </div>
            </div>
            <div className={classes.proofPanel}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>Comprobante</Typography>
              <div className={classes.proofBox}>
                <div className={classes.proofContent}>
                  <div>
                    <Typography variant="body2">Comprobante de pago</Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      className={classes.proofName}
                    >
                      {proofName || "Sin comprobante"}
                    </Typography>
                  </div>
                  <div className={classes.proofActions}>
                    <Button component="label" variant="outlined" size="small">
                      {proofName ? "Reemplazar" : "Adjuntar"}
                      <input
                        type="file"
                        hidden
                        accept="image/*,.pdf"
                        onChange={event => {
                          const file = event.target.files?.[0] || null;
                          setProofFile(file);
                          if (file) setProofRemoved(false);
                        }}
                      />
                    </Button>
                    {proofHref && (
                      <Button
                        variant="outlined"
                        size="small"
                        href={proofHref}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver
                      </Button>
                    )}
                    {proofName && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        onClick={() => {
                          setProofFile(null);
                          setProofRemoved(Boolean(orderForm.paymentProofUrl));
                        }}
                      >
                        Eliminar
                      </Button>
                    )}
                  </div>
                </div>
                <div className={classes.proofPreviewBox}>
                  {proofPreviewUrl && proofIsImage ? (
                    proofFilePreviewUrl ? (
                      <img
                        src={proofFilePreviewUrl}
                        alt="Comprobante"
                        className={classes.proofPreview}
                      />
                    ) : (
                      <ModalImageCors
                        imageUrl={proofPreviewUrl}
                        className={classes.proofPreview}
                      />
                    )
                  ) : proofPreviewHref && proofIsPdf ? (
                    <iframe
                      title={proofName || "Comprobante PDF"}
                      className={classes.proofPdfPreview}
                      src={proofPreviewHref}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {proofName || "Sin archivo adjunto"}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          </div>

          <TextField label="Notas del cliente" value={orderForm.customerNotes || ""} onChange={event => setOrderForm({ ...orderForm, customerNotes: event.target.value })} variant="outlined" margin="dense" multiline minRows={2} fullWidth />
          <TextField label="Notas internas" value={orderForm.internalNotes || ""} onChange={event => setOrderForm({ ...orderForm, internalNotes: event.target.value })} variant="outlined" margin="dense" multiline minRows={2} fullWidth />
        </DialogContent>
        <DialogActions>
          {orderForm.id && <Button color="secondary" variant="outlined" startIcon={<DeleteOutline />} onClick={() => setDeletingOrder(orderForm)}>Eliminar</Button>}
          <Button onClick={() => setOrderModalOpen(false)} color="secondary" variant="outlined">Cancelar</Button>
          <Button onClick={saveOrder} color="primary" variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={methodModalOpen} onClose={closeMethodModal} maxWidth="sm" fullWidth>
        <DialogTitle>{methodForm.id ? "Editar metodo" : "Nuevo metodo de pago"}</DialogTitle>
        <DialogContent dividers>
          <TextField label="Nombre" value={methodForm.name} onChange={event => setMethodForm({ ...methodForm, name: event.target.value })} variant="outlined" margin="dense" fullWidth />
          <TextField label="Descripcion" value={methodForm.description} onChange={event => setMethodForm({ ...methodForm, description: event.target.value })} variant="outlined" margin="dense" fullWidth />
          <TextField label="Instrucciones" value={methodForm.instructions} onChange={event => setMethodForm({ ...methodForm, instructions: event.target.value })} variant="outlined" margin="dense" multiline minRows={3} fullWidth />
          <FormControlLabel control={<Checkbox color="primary" checked={methodForm.requiresProof} onChange={event => setMethodForm({ ...methodForm, requiresProof: event.target.checked })} />} label="Requiere comprobante" />
          <FormControlLabel control={<Checkbox color="primary" checked={methodForm.isActive !== false} onChange={event => setMethodForm({ ...methodForm, isActive: event.target.checked })} />} label="Activo" />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMethodModal} color="secondary" variant="outlined">Cancelar</Button>
          <Button onClick={saveMethod} color="primary" variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={shippingMethodModalOpen}
        onClose={closeShippingMethodModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {shippingMethodForm.id ? "Editar metodo de envio" : "Nuevo metodo de envio"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nombre"
            value={shippingMethodForm.name}
            onChange={event =>
              setShippingMethodForm({ ...shippingMethodForm, name: event.target.value })
            }
            variant="outlined"
            margin="dense"
            fullWidth
          />
          <TextField
            label="Descripcion"
            value={shippingMethodForm.description}
            onChange={event =>
              setShippingMethodForm({
                ...shippingMethodForm,
                description: event.target.value,
              })
            }
            variant="outlined"
            margin="dense"
            fullWidth
          />
          <TextField
            label="Costo"
            type="number"
            value={shippingMethodForm.cost}
            onChange={event =>
              setShippingMethodForm({ ...shippingMethodForm, cost: event.target.value })
            }
            variant="outlined"
            margin="dense"
            fullWidth
          />
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Campos del metodo de envio
          </Typography>
          {normalizeShippingFields(shippingMethodForm.fields).map((field, index) => (
            <div className={classes.productOptionBox} key={index}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={5}>
                  <TextField
                    label="Dato"
                    value={field.name || ""}
                    onChange={event => updateShippingField(index, { name: event.target.value })}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      label="Tipo"
                      value={field.type || "text"}
                      onChange={event =>
                        updateShippingField(index, {
                          type: event.target.value,
                          options:
                            ["select", "multiple"].includes(event.target.value) &&
                            !field.options?.length
                              ? [{ name: "", priceDelta: 0 }]
                              : field.options,
                        })
                      }
                    >
                      {checkoutFieldTypes.map(type => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={8} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={field.required !== false}
                        onChange={event => updateShippingField(index, { required: event.target.checked })}
                      />
                    }
                    label="Requerido"
                  />
                </Grid>
                <Grid item xs={4} md={1}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setShippingMethodForm({
                        ...shippingMethodForm,
                        fields: normalizeShippingFields(shippingMethodForm.fields).filter(
                          (_, fieldIndex) => fieldIndex !== index
                        ),
                      })
                    }
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
              {["select", "multiple"].includes(field.type) && (
                <div className={classes.checkoutOptionList}>
                  {normalizeShippingFieldOptions(field.options, true).map((option, optionIndex) => (
                    <Grid container spacing={1} alignItems="center" key={optionIndex}>
                      <Grid item xs={7}>
                        <TextField
                          label="Opcion"
                          value={option.name || ""}
                          onChange={event =>
                            updateShippingFieldOption(index, optionIndex, { name: event.target.value })
                          }
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="Costo"
                          type="number"
                          value={option.priceDelta || 0}
                          onChange={event =>
                            updateShippingFieldOption(index, optionIndex, { priceDelta: event.target.value })
                          }
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const fields = normalizeShippingFields(shippingMethodForm.fields);
                            fields[index] = {
                              ...fields[index],
                              options: normalizeShippingFieldOptions(fields[index].options, true).filter(
                                (_, currentIndex) => currentIndex !== optionIndex
                              ),
                            };
                            setShippingMethodForm({ ...shippingMethodForm, fields });
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                  <Button
                    color="primary"
                    size="small"
                    onClick={() => {
                      const fields = normalizeShippingFields(shippingMethodForm.fields);
                      fields[index] = {
                        ...fields[index],
                        options: [
                          ...normalizeShippingFieldOptions(fields[index].options, true),
                          { name: "", priceDelta: 0 },
                        ],
                      };
                      setShippingMethodForm({ ...shippingMethodForm, fields });
                    }}
                  >
                    Agregar opcion
                  </Button>
                </div>
              )}
              {renderShippingFieldConditionalControls(field, index)}
            </div>
          ))}
          <Button
            color="primary"
            onClick={() =>
              setShippingMethodForm({
                ...shippingMethodForm,
                fields: [...normalizeShippingFields(shippingMethodForm.fields), emptyShippingField()],
              })
            }
          >
            Agregar campo
          </Button>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={shippingMethodForm.requiresLocation === true}
                onChange={event =>
                  setShippingMethodForm({
                    ...shippingMethodForm,
                    requiresLocation: event.target.checked,
                  })
                }
              />
            }
            label="Solicitar ubicacion de Maps"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={shippingMethodForm.isActive !== false}
                onChange={event =>
                  setShippingMethodForm({
                    ...shippingMethodForm,
                    isActive: event.target.checked,
                  })
                }
              />
            }
            label="Activo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeShippingMethodModal} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={saveShippingMethod} color="primary" variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={giftCardModalOpen} onClose={() => setGiftCardModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{giftCardForm.id ? "Editar Gift Card" : "Nueva Gift Card"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Codigo"
                value={giftCardForm.code}
                onChange={event =>
                  setGiftCardForm({
                    ...giftCardForm,
                    code: event.target.value.toUpperCase(),
                  })
                }
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Saldo inicial"
                type="number"
                value={giftCardForm.initialBalance}
                onChange={event =>
                  setGiftCardForm({
                    ...giftCardForm,
                    initialBalance: event.target.value,
                    balance: giftCardForm.id ? giftCardForm.balance : event.target.value,
                  })
                }
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Saldo disponible"
                type="number"
                value={giftCardForm.balance}
                onChange={event =>
                  setGiftCardForm({ ...giftCardForm, balance: event.target.value })
                }
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Vence"
                type="date"
                value={giftCardForm.expiresAt || ""}
                onChange={event =>
                  setGiftCardForm({ ...giftCardForm, expiresAt: event.target.value })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notas"
                value={giftCardForm.notes || ""}
                onChange={event =>
                  setGiftCardForm({ ...giftCardForm, notes: event.target.value })
                }
                variant="outlined"
                margin="dense"
                multiline
                minRows={2}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={giftCardForm.isActive !== false}
                    onChange={event =>
                      setGiftCardForm({
                        ...giftCardForm,
                        isActive: event.target.checked,
                      })
                    }
                  />
                }
                label="Activa"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGiftCardModalOpen(false)} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={saveGiftCard} color="primary" variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={statusModalOpen} onClose={() => setStatusModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{statusForm.id ? "Editar estado" : "Nuevo estado"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TextField label="Nombre" value={statusForm.name} onChange={event => setStatusForm({ ...statusForm, name: event.target.value })} variant="outlined" margin="dense" fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Clave" value={statusForm.key} onChange={event => setStatusForm({ ...statusForm, key: event.target.value })} variant="outlined" margin="dense" fullWidth /></Grid>
            <Grid item xs={12} md={6}><TextField label="Orden" type="number" value={statusForm.position} onChange={event => setStatusForm({ ...statusForm, position: event.target.value })} variant="outlined" margin="dense" fullWidth /></Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Color"
                value={statusForm.color}
                onChange={event =>
                  setStatusForm({ ...statusForm, color: event.target.value })
                }
                onBlur={event => {
                  if (!isValidHexColor(event.target.value)) return;
                  const color = normalizeHexColor(event.target.value);
                  setStatusForm(prev => ({ ...prev, color }));
                  saveCustomColor(color);
                }}
                variant="outlined"
                margin="dense"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <div
                        className={classes.colorAdornment}
                        style={{ backgroundColor: statusForm.color }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => setStatusColorPickerOpen(true)}
                    >
                      <Colorize />
                    </IconButton>
                  ),
                }}
              />
              <ColorPicker
                open={statusColorPickerOpen}
                currentColor={statusForm.color}
                handleClose={() => setStatusColorPickerOpen(false)}
                onChange={color => setStatusForm(prev => ({ ...prev, color }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Secuencia</InputLabel>
                <Select
                  value={statusForm.sequenceId ? String(statusForm.sequenceId) : NO_SEQUENCE}
                  label="Secuencia"
                  onChange={event => {
                    if (event.target.value === "__create__") {
                      openCreateSequence(sequenceId =>
                        setStatusForm(prev => ({ ...prev, sequenceId }))
                      );
                      return;
                    }
                    setStatusForm({
                      ...statusForm,
                      sequenceId:
                        event.target.value === NO_SEQUENCE ? "" : event.target.value,
                    });
                  }}
                >
                  <MenuItem value={NO_SEQUENCE}>Sin secuencia</MenuItem>
                  <MenuItem value="__create__">Crear secuencia...</MenuItem>
                  {sequences
                    .filter(sequence =>
                      String(sequence.connectionId || "") === String(selectedConnectionId || "") ||
                      String(sequence.id) === String(statusForm.sequenceId || "")
                    )
                    .map(sequence => (
                      <MenuItem key={sequence.id} value={String(sequence.id)}>
                        {formatSequenceOption(sequence)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <div className={classes.paymentMethodActions}>
                <Button
                  color="primary"
                  disabled={!statusForm.sequenceId}
                  onClick={() =>
                    openEditSequence(statusForm.sequenceId, sequenceId =>
                      setStatusForm(prev => ({ ...prev, sequenceId }))
                    )
                  }
                >
                  Editar secuencia
                </Button>
              </div>
            </Grid>
          </Grid>
          <FormControlLabel control={<Checkbox color="primary" checked={statusForm.isFinal} onChange={event => setStatusForm({ ...statusForm, isFinal: event.target.checked })} />} label="Estado final" />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={statusForm.triggersStreamingFulfillment === true}
                onChange={event =>
                  setStatusForm({
                    ...statusForm,
                    triggersStreamingFulfillment: event.target.checked,
                  })
                }
              />
            }
            label="Activar entrega y asignacion de Gift Card y Streaming al entrar en este estado"
          />
          <FormControlLabel control={<Checkbox color="primary" checked={statusForm.isActive !== false} onChange={event => setStatusForm({ ...statusForm, isActive: event.target.checked })} />} label="Activo" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusModalOpen(false)} color="secondary" variant="outlined">Cancelar</Button>
          <Button onClick={saveStatus} color="primary" variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={categoryModalOpen} onClose={() => setCategoryModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{categoryForm.id ? "Editar categoria" : "Nueva categoria"}</DialogTitle>
        <DialogContent dividers>
          <TextField label="Nombre" value={categoryForm.name} onChange={event => setCategoryForm({ ...categoryForm, name: event.target.value })} variant="outlined" margin="dense" fullWidth />
          <TextField label="Descripcion" value={categoryForm.description} onChange={event => setCategoryForm({ ...categoryForm, description: event.target.value })} variant="outlined" margin="dense" multiline minRows={2} fullWidth />
          <Paper className={classes.productMessagePreview} elevation={0}>
            <div className={classes.productPreviewHeader}>
              <Typography variant="subtitle2">Imagen de categoria</Typography>
            </div>
            <div className={classes.productPreviewBody}>
              <div className={classes.productBubble}>
                {categoryImageSrc ? (
                  <img
                    className={classes.productPreviewImage}
                    src={categoryImageSrc}
                    alt={categoryImageName || categoryForm.name || "Categoria"}
                  />
                ) : (
                  <div className={classes.productPreviewImage} />
                )}
                <Typography variant="body2" className={classes.productPreviewText}>
                  {categoryForm.description || categoryForm.name || "Vista previa de categoria"}
                </Typography>
              </div>
            </div>
            <div className={classes.productFileActions}>
              <div className={classes.productFilePicker}>
                <input
                  accept="image/*,.webp"
                  id="order-category-image"
                  type="file"
                  style={{ display: "none" }}
                  onChange={event => {
                    const file = event.target.files?.[0] || null;
                    setCategoryImageFile(file);
                  }}
                />
                <label htmlFor="order-category-image">
                  <Button component="span" variant="outlined" size="small">
                    Seleccionar archivo
                  </Button>
                </label>
                {categoryImageName && (
                  <Typography variant="body2" className={classes.productFileName}>
                    {categoryImageName}
                  </Typography>
                )}
              </div>
              {(categoryImageFile || categoryForm.imageUrl) && (
                <Button
                  size="small"
                  onClick={() => {
                    setCategoryImageFile(null);
                    setCategoryForm({ ...categoryForm, imageUrl: "" });
                  }}
                >
                  Quitar
                </Button>
              )}
            </div>
          </Paper>
          <TextField label="Orden" type="number" value={categoryForm.position} onChange={event => setCategoryForm({ ...categoryForm, position: event.target.value })} variant="outlined" margin="dense" fullWidth />
          <FormControlLabel control={<Checkbox color="primary" checked={categoryForm.isActive !== false} onChange={event => setCategoryForm({ ...categoryForm, isActive: event.target.checked })} />} label="Activa" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryModalOpen(false)} color="secondary" variant="outlined">Cancelar</Button>
          <Button onClick={saveCategory} color="primary" variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={productModalOpen} onClose={() => setProductModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{productForm.id ? "Editar producto" : "Nuevo producto"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><TextField label="Nombre" value={productForm.name} onChange={event => setProductForm({ ...productForm, name: event.target.value })} variant="outlined" margin="dense" fullWidth /></Grid>
            <Grid item xs={12} md={3}><TextField label="SKU" value={productForm.sku} onChange={event => setProductForm({ ...productForm, sku: event.target.value })} variant="outlined" margin="dense" fullWidth /></Grid>
            <Grid item xs={12} md={3}><TextField label="Precio" type="number" value={productForm.price} onChange={event => setProductForm({ ...productForm, price: event.target.value })} variant="outlined" margin="dense" fullWidth /></Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Categoria</InputLabel>
                <Select value={productForm.categoryId || "no-category"} label="Categoria" onChange={event => setProductForm({ ...productForm, categoryId: event.target.value === "no-category" ? "" : event.target.value })}>
                  <MenuItem value="no-category">Sin categoria</MenuItem>
                  {productCategories.map(category => <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}><TextField label="Stock" type="number" value={productForm.stock} onChange={event => setProductForm({ ...productForm, stock: event.target.value })} variant="outlined" margin="dense" fullWidth /></Grid>
            <Grid item xs={12} md={3}><TextField label="Orden" type="number" value={productForm.position} onChange={event => setProductForm({ ...productForm, position: event.target.value })} variant="outlined" margin="dense" fullWidth /></Grid>
            <Grid item xs={12}><TextField label="Descripcion" value={productForm.description} onChange={event => setProductForm({ ...productForm, description: event.target.value })} variant="outlined" margin="dense" multiline minRows={2} fullWidth /></Grid>
            <Grid item xs={12}>
              <Paper className={classes.productMessagePreview} elevation={0}>
                <div className={classes.productPreviewHeader}>
                  <Typography variant="subtitle2">Vista previa del producto</Typography>
                </div>
                <div className={classes.productPreviewBody}>
                  <div className={classes.productBubble}>
                    {productImageSrc ? (
                      <img
                        className={classes.productPreviewImage}
                        src={productImageSrc}
                        alt={productImageName || productForm.name || "Producto"}
                      />
                    ) : (
                      <div className={classes.productPreviewImage} />
                    )}
                    <Typography variant="body2" className={classes.productPreviewText}>
                      {productMessageBody}
                    </Typography>
                  </div>
                </div>
                <div className={classes.productFileActions}>
                  <div className={classes.productFilePicker}>
                    <input
                      accept="image/*,.webp"
                      id="order-product-image"
                      type="file"
                      style={{ display: "none" }}
                      onChange={event => {
                        const file = event.target.files?.[0] || null;
                        setProductImageFile(file);
                      }}
                    />
                    <label htmlFor="order-product-image">
                      <Button component="span" variant="outlined" size="small">
                        Seleccionar archivo
                      </Button>
                    </label>
                    {productImageName && (
                      <Typography variant="body2" className={classes.productFileName}>
                        {productImageName}
                      </Typography>
                    )}
                  </div>
                  {(productImageFile || productForm.imageUrl) && (
                    <Button
                      size="small"
                      onClick={() => {
                        setProductImageFile(null);
                        setProductForm({ ...productForm, imageUrl: "" });
                      }}
                    >
                      Quitar
                    </Button>
                  )}
                </div>
                <Typography variant="caption" color="textSecondary">
                  Imagenes para mostrar el producto en el mensaje del carrito.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          <FormControlLabel control={<Checkbox color="primary" checked={productForm.isActive !== false} onChange={event => setProductForm({ ...productForm, isActive: event.target.checked })} />} label="Activo" />
          <Divider style={{ margin: "12px 0" }} />
          <Typography variant="subtitle2" className={classes.sectionTitle}>Flujo de producto</Typography>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={productForm.salesFlowConfig?.enabled === true}
                onChange={event =>
                  updateSalesFlowConfig({
                    enabled: event.target.checked,
                    options: productForm.salesFlowConfig?.options?.length
                      ? productForm.salesFlowConfig.options
                      : [emptySalesFlowOption()],
                  })
                }
              />
            }
            label="Mostrar opciones comerciales cuando una secuencia inicia este producto"
          />
          {productForm.salesFlowConfig?.enabled === true && (
            <>
              {(productForm.salesFlowConfig?.options?.length
                ? productForm.salesFlowConfig.options
                : [emptySalesFlowOption()]
              ).map((option, optionIndex) => (
                <Paper key={optionIndex} variant="outlined" style={{ padding: 12, marginBottom: 8 }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <TextField
                        label="Opcion del menu"
                        value={option.label || ""}
                        onChange={event =>
                          updateSalesFlowOption(optionIndex, { label: event.target.value })
                        }
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl variant="outlined" margin="dense" fullWidth>
                        <InputLabel>Secuencia</InputLabel>
                        <Select
                          value={option.sequenceId || NO_SEQUENCE}
                          label="Secuencia"
                          onChange={event => {
                            if (event.target.value === "__create__") {
                              openCreateSequence(sequenceId =>
                                updateSalesFlowOption(optionIndex, { sequenceId })
                              );
                              return;
                            }
                            updateSalesFlowOption(optionIndex, {
                              sequenceId:
                                event.target.value === NO_SEQUENCE
                                  ? NO_SEQUENCE
                                  : event.target.value,
                            });
                          }}
                        >
                          <MenuItem value={NO_SEQUENCE}>Sin secuencia</MenuItem>
                          <MenuItem value="__create__">Crear secuencia...</MenuItem>
                          {sequences
                            .filter(sequence =>
                              String(sequence.connectionId || "") === String(selectedConnectionId || "") ||
                              String(sequence.id) === String(option.sequenceId || "")
                            )
                            .map(sequence => (
                              <MenuItem key={sequence.id} value={String(sequence.id)}>
                                {formatSequenceOption(sequence)}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <div className={classes.paymentMethodActions}>
                        <Button
                          color="primary"
                          disabled={!option.sequenceId || option.sequenceId === NO_SEQUENCE}
                          onClick={() =>
                            openEditSequence(option.sequenceId, sequenceId =>
                              updateSalesFlowOption(optionIndex, { sequenceId })
                            )
                          }
                        >
                          Editar secuencia
                        </Button>
                      </div>
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          updateSalesFlowConfig({
                            options: (productForm.salesFlowConfig?.options || []).filter(
                              (_, index) => index !== optionIndex
                            ),
                          })
                        }
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
              <Button
                color="primary"
                startIcon={<Add />}
                onClick={() =>
                  updateSalesFlowConfig({
                    options: [
                      ...(productForm.salesFlowConfig?.options || []),
                      emptySalesFlowOption(),
                    ],
                  })
                }
              >
                Agregar opcion comercial
              </Button>
            </>
          )}
          <Divider style={{ margin: "12px 0" }} />
          <Typography variant="subtitle2" className={classes.sectionTitle}>Entregas streaming</Typography>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={productForm.streamingConfig?.enabled === true}
                onChange={event =>
                  updateStreamingConfig({
                    enabled: event.target.checked,
                    items: productForm.streamingConfig?.items?.length
                      ? productForm.streamingConfig.items
                      : [emptyStreamingConfigItem()],
                  })
                }
              />
            }
            label="Este producto entrega perfiles o cuentas de streaming"
          />
          {productForm.streamingConfig?.enabled === true && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={productForm.streamingConfig?.sendAccess !== false}
                    onChange={event => updateStreamingConfig({ sendAccess: event.target.checked })}
                  />
                }
                label="Enviar acceso automaticamente al completar el pedido"
              />
              {(productForm.streamingConfig?.items?.length
                ? productForm.streamingConfig.items
                : [emptyStreamingConfigItem()]
              ).map((item, streamingIndex) => (
                <Paper key={streamingIndex} variant="outlined" style={{ padding: 12, marginBottom: 8 }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <FormControl variant="outlined" margin="dense" fullWidth>
                        <InputLabel>Plataforma</InputLabel>
                        <Select
                          value={item.platformId || NO_STREAMING_PLATFORM}
                          label="Plataforma"
                          onChange={event => updateStreamingConfigItem(streamingIndex, { platformId: event.target.value })}
                        >
                          <MenuItem value={NO_STREAMING_PLATFORM}>Selecciona plataforma</MenuItem>
                          {streamingPlatforms.map(platform => (
                            <MenuItem key={platform.id} value={platform.id}>
                              {platform.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl variant="outlined" margin="dense" fullWidth>
                        <InputLabel>Tipo</InputLabel>
                        <Select
                          value={item.accessType || "profile"}
                          label="Tipo"
                          onChange={event => updateStreamingConfigItem(streamingIndex, { accessType: event.target.value })}
                        >
                          <MenuItem value="profile">Perfil</MenuItem>
                          <MenuItem value="account">Cuenta completa</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl variant="outlined" margin="dense" fullWidth>
                        <InputLabel>Duracion</InputLabel>
                        <Select
                          value={item.durationMode || "fixed"}
                          label="Duracion"
                          onChange={event => updateStreamingConfigItem(streamingIndex, { durationMode: event.target.value })}
                        >
                          <MenuItem value="fixed">Dias fijos</MenuItem>
                          <MenuItem value="option">Desde opcion</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        label="Dias"
                        type="number"
                        value={item.durationDays || 30}
                        onChange={event => updateStreamingConfigItem(streamingIndex, { durationDays: event.target.value })}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl variant="outlined" margin="dense" fullWidth>
                        <InputLabel>Opcion</InputLabel>
                        <Select
                          value={item.optionName || NO_STREAMING_OPTION}
                          label="Opcion"
                          onChange={event => updateStreamingConfigItem(streamingIndex, { optionName: event.target.value })}
                          disabled={item.durationMode !== "option"}
                        >
                          <MenuItem value={NO_STREAMING_OPTION}>Sin opcion</MenuItem>
                          {(productForm.options || []).map(option => (
                            <MenuItem key={option.name || "opcion"} value={option.name || NO_STREAMING_OPTION}>
                              {option.name || "Opcion sin nombre"}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          updateStreamingConfig({
                            items: (productForm.streamingConfig?.items || []).filter(
                              (_, index) => index !== streamingIndex
                            ),
                          })
                        }
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
              <Button
                color="primary"
                startIcon={<Add />}
                onClick={() =>
                  updateStreamingConfig({
                    items: [...(productForm.streamingConfig?.items || []), emptyStreamingConfigItem()],
                  })
                }
              >
                Agregar entrega streaming
              </Button>
            </>
          )}
          <Divider style={{ margin: "12px 0" }} />
          <Typography variant="subtitle2" className={classes.sectionTitle}>Gift Cards</Typography>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={productForm.giftCardConfig?.enabled === true}
                onChange={event =>
                  updateGiftCardConfig({
                    enabled: event.target.checked,
                  })
                }
              />
            }
            label="Este producto genera o recarga Gift Cards automaticamente"
          />
          {productForm.giftCardConfig?.enabled === true && (
            <Paper variant="outlined" style={{ padding: 12, marginBottom: 8 }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={3}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>Accion</InputLabel>
                    <Select
                      value={productForm.giftCardConfig?.action || "create"}
                      label="Accion"
                      onChange={event => updateGiftCardConfig({ action: event.target.value })}
                    >
                      <MenuItem value="create">Crear nueva</MenuItem>
                      <MenuItem value="recharge">Recargar existente</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>Monto</InputLabel>
                    <Select
                      value={productForm.giftCardConfig?.amountMode || "price"}
                      label="Monto"
                      onChange={event => updateGiftCardConfig({ amountMode: event.target.value })}
                    >
                      <MenuItem value="price">Precio del producto</MenuItem>
                      <MenuItem value="fixed">Monto fijo</MenuItem>
                      <MenuItem value="option">Desde opcion</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Monto fijo"
                    type="number"
                    value={productForm.giftCardConfig?.amount || 0}
                    onChange={event => updateGiftCardConfig({ amount: event.target.value })}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    disabled={productForm.giftCardConfig?.amountMode !== "fixed"}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>Opcion</InputLabel>
                    <Select
                      value={productForm.giftCardConfig?.optionName || NO_GIFT_CARD_OPTION}
                      label="Opcion"
                      onChange={event => updateGiftCardConfig({ optionName: event.target.value })}
                      disabled={productForm.giftCardConfig?.amountMode !== "option"}
                    >
                      <MenuItem value={NO_GIFT_CARD_OPTION}>Sin opcion</MenuItem>
                      {(productForm.options || []).map(option => (
                        <MenuItem key={option.name || "gift-card-option"} value={option.name || NO_GIFT_CARD_OPTION}>
                          {option.name || "Opcion sin nombre"}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={productForm.giftCardConfig?.sendAccess !== false}
                        onChange={event => updateGiftCardConfig({ sendAccess: event.target.checked })}
                      />
                    }
                    label="Enviar codigo o confirmacion automaticamente al completar el pedido"
                  />
                </Grid>
              </Grid>
            </Paper>
          )}
          <Typography variant="subtitle2" className={classes.sectionTitle}>Opciones del producto</Typography>
          {(productForm.options || []).map((option, optionIndex) => (
            <div key={optionIndex} className={classes.productOptionBox}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={5}><TextField label="Nombre de opcion" value={option.name} onChange={event => updateProductOption(optionIndex, { name: event.target.value })} variant="outlined" margin="dense" fullWidth /></Grid>
                <Grid item xs={6} md={3}><FormControlLabel control={<Checkbox color="primary" checked={option.required === true} onChange={event => updateProductOption(optionIndex, { required: event.target.checked })} />} label="Requerida" /></Grid>
                <Grid item xs={6} md={3}><FormControlLabel control={<Checkbox color="primary" checked={option.multiple === true} onChange={event => updateProductOption(optionIndex, { multiple: event.target.checked })} />} label="Multiple" /></Grid>
                <Grid item xs={12} md={1}><IconButton size="small" onClick={() => setProductForm({ ...productForm, options: productForm.options.filter((_, index) => index !== optionIndex) })}><DeleteOutline fontSize="small" /></IconButton></Grid>
              </Grid>
              {renderProductOptionConditionalControls(option, optionIndex)}
              {(option.values || []).map((value, valueIndex) => (
                <Grid container spacing={1} alignItems="center" key={valueIndex}>
                  <Grid item xs={8}><TextField label="Valor" value={value.name} onChange={event => updateProductOptionValue(optionIndex, valueIndex, { name: event.target.value })} variant="outlined" margin="dense" fullWidth /></Grid>
                  <Grid item xs={3}><TextField label="Extra" type="number" value={value.priceDelta || 0} onChange={event => updateProductOptionValue(optionIndex, valueIndex, { priceDelta: event.target.value })} variant="outlined" margin="dense" fullWidth /></Grid>
                  <Grid item xs={1}><IconButton size="small" onClick={() => updateProductOption(optionIndex, { values: option.values.filter((_, index) => index !== valueIndex) })}><DeleteOutline fontSize="small" /></IconButton></Grid>
                </Grid>
              ))}
              <Button color="primary" onClick={() => updateProductOption(optionIndex, { values: [...(option.values || []), { name: "", priceDelta: 0 }] })}>Agregar valor</Button>
            </div>
          ))}
          <Button color="primary" startIcon={<Add />} onClick={() => setProductForm({ ...productForm, options: [...(productForm.options || []), emptyProductOption()] })}>Agregar opcion</Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductModalOpen(false)} color="secondary" variant="outlined">Cancelar</Button>
          <Button onClick={saveProduct} color="primary" variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={upsellModalOpen} onClose={() => setUpsellModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{upsellForm.id ? "Editar upsell" : "Nuevo upsell"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nombre"
                value={upsellForm.name}
                onChange={event => setUpsellForm({ ...upsellForm, name: event.target.value })}
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Orden"
                type="number"
                value={upsellForm.position}
                onChange={event => setUpsellForm({ ...upsellForm, position: event.target.value })}
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Cantidad sugerida"
                type="number"
                value={upsellForm.quantity}
                onChange={event => setUpsellForm({ ...upsellForm, quantity: event.target.value })}
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Mensaje"
                value={upsellForm.message}
                onChange={event => setUpsellForm({ ...upsellForm, message: event.target.value })}
                variant="outlined"
                margin="dense"
                multiline
                minRows={2}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Producto ofrecido</InputLabel>
                <Select
                  value={upsellForm.offeredProductId || NO_CATALOG_PRODUCT}
                  label="Producto ofrecido"
                  onChange={event => setUpsellForm({ ...upsellForm, offeredProductId: event.target.value })}
                >
                  <MenuItem value={NO_CATALOG_PRODUCT}>Selecciona un producto</MenuItem>
                  {products.map(product => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name}
                      {product.price ? ` - ${money(product.price)}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Productos donde aplica</InputLabel>
                <Select
                  multiple
                  value={
                    (upsellForm.targetProductIds || []).length
                      ? upsellForm.targetProductIds
                      : [ALL_UPSELL_PRODUCTS]
                  }
                  label="Productos donde aplica"
                  renderValue={selected =>
                    selected.includes(ALL_UPSELL_PRODUCTS)
                      ? "Todo producto"
                      : selected
                          .map(productId => products.find(product => String(product.id) === String(productId))?.name)
                          .filter(Boolean)
                          .join(", ")
                  }
                  onChange={event => {
                    const value = event.target.value || [];
                    setUpsellForm({
                      ...upsellForm,
                      targetProductIds:
                        value.includes(ALL_UPSELL_PRODUCTS) && value.length === 1
                          ? []
                          : value.filter(item => item !== ALL_UPSELL_PRODUCTS),
                    });
                  }}
                >
                  <MenuItem value={ALL_UPSELL_PRODUCTS}>
                    <Checkbox checked={!(upsellForm.targetProductIds || []).length} />
                    <ListItemText primary="Todo producto" />
                  </MenuItem>
                  {products.map(product => (
                    <MenuItem key={product.id} value={product.id}>
                      <Checkbox
                        checked={(upsellForm.targetProductIds || []).some(
                          productId => String(productId) === String(product.id)
                        )}
                      />
                      <ListItemText primary={product.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Categorias donde aplica</InputLabel>
                <Select
                  multiple
                  value={
                    (upsellForm.targetCategoryIds || []).length
                      ? upsellForm.targetCategoryIds
                      : [ALL_UPSELL_CATEGORIES]
                  }
                  label="Categorias donde aplica"
                  renderValue={selected =>
                    selected.includes(ALL_UPSELL_CATEGORIES)
                      ? "Toda categoria"
                      : selected
                          .map(categoryId => productCategories.find(category => String(category.id) === String(categoryId))?.name)
                          .filter(Boolean)
                          .join(", ")
                  }
                  onChange={event => {
                    const value = event.target.value || [];
                    setUpsellForm({
                      ...upsellForm,
                      targetCategoryIds:
                        value.includes(ALL_UPSELL_CATEGORIES) && value.length === 1
                          ? []
                          : value.filter(item => item !== ALL_UPSELL_CATEGORIES),
                    });
                  }}
                >
                  <MenuItem value={ALL_UPSELL_CATEGORIES}>
                    <Checkbox checked={!(upsellForm.targetCategoryIds || []).length} />
                    <ListItemText primary="Toda categoria" />
                  </MenuItem>
                  {productCategories.map(category => (
                    <MenuItem key={category.id} value={category.id}>
                      <Checkbox
                        checked={(upsellForm.targetCategoryIds || []).some(
                          categoryId => String(categoryId) === String(category.id)
                        )}
                      />
                      <ListItemText primary={category.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="textSecondary">
                Si no seleccionas productos ni categorias, el upsell aplica a cualquier carrito.
              </Typography>
            </Grid>
          </Grid>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={upsellForm.isActive !== false}
                onChange={event => setUpsellForm({ ...upsellForm, isActive: event.target.checked })}
              />
            }
            label="Activo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpsellModalOpen(false)} color="secondary" variant="outlined">Cancelar</Button>
          <Button onClick={saveUpsell} color="primary" variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal title="Eliminar pedido" open={Boolean(deletingOrder)} onClose={() => setDeletingOrder(null)} onConfirm={() => deleteResource("order", deletingOrder)}>Esta accion no se puede deshacer.</ConfirmationModal>
      <ConfirmationModal
        title={`Eliminar ${selectedOrderCount} pedido${
          selectedOrderCount === 1 ? "" : "s"
        }?`}
        open={bulkDeleteOpen}
        onClose={setBulkDeleteOpen}
        onConfirm={handleDeleteSelectedOrders}
      >
        Esta accion no se puede deshacer.
      </ConfirmationModal>
      <ConfirmationModal title="Eliminar metodo" open={Boolean(deletingMethod)} onClose={() => setDeletingMethod(null)} onConfirm={() => deleteResource("method", deletingMethod)}>Esta accion no se puede deshacer.</ConfirmationModal>
      <ConfirmationModal title="Eliminar metodo de envio" open={Boolean(deletingShippingMethod)} onClose={() => setDeletingShippingMethod(null)} onConfirm={() => deleteResource("shippingMethod", deletingShippingMethod)}>Esta accion no se puede deshacer.</ConfirmationModal>
      <ConfirmationModal title="Eliminar estado" open={Boolean(deletingStatus)} onClose={() => setDeletingStatus(null)} onConfirm={() => deleteResource("status", deletingStatus)}>Esta accion no se puede deshacer.</ConfirmationModal>
      <ConfirmationModal title="Eliminar categoria" open={Boolean(deletingCategory)} onClose={() => setDeletingCategory(null)} onConfirm={() => deleteResource("category", deletingCategory)}>Esta accion no se puede deshacer.</ConfirmationModal>
      <ConfirmationModal title="Eliminar producto" open={Boolean(deletingProduct)} onClose={() => setDeletingProduct(null)} onConfirm={() => deleteResource("product", deletingProduct)}>Esta accion no se puede deshacer.</ConfirmationModal>
      <ConfirmationModal title="Eliminar upsell" open={Boolean(deletingUpsell)} onClose={() => setDeletingUpsell(null)} onConfirm={() => deleteResource("upsell", deletingUpsell)}>Esta accion no se puede deshacer.</ConfirmationModal>
      <ConfirmationModal title="Eliminar Gift Card" open={Boolean(deletingGiftCard)} onClose={() => setDeletingGiftCard(null)} onConfirm={() => deleteResource("giftCard", deletingGiftCard)}>Esta accion no se puede deshacer.</ConfirmationModal>
      <ConfirmationModal
        title="Eliminar campo de checkout"
        open={deletingCheckoutFieldIndex !== null && deletingCheckoutFieldIndex !== undefined}
        onClose={() => setDeletingCheckoutFieldIndex(null)}
        onConfirm={deleteCheckoutField}
      >
        Esta accion guardara la configuracion inmediatamente.
      </ConfirmationModal>
      <ConfirmationModal
        title="Restablecer plantilla"
        open={templateResetOpen}
        onClose={() => setTemplateResetOpen(false)}
        onConfirm={resetTemplate}
      >
        Se reemplazaran el catalogo, metodos de pago, metodos de envio, estados y campos de checkout de esta conexion con una plantilla completa de prueba. Los pedidos existentes no se eliminan.
      </ConfirmationModal>
      {sequenceModalOpen && (
        <ChatbotModal
          open={sequenceModalOpen}
          onClose={handleSequenceModalClose}
          aria-labelledby="form-dialog-title"
          sequenceId={sequenceModalId}
          defaultConnectionId={selectedConnectionId}
          onSave={handleSequenceSaved}
        />
      )}
    </MainContainer>
  );
};

export default Orders;
